import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { cloudSave, cloudLoad } from "../lib/cloudStore";

/* ============================================================================
   RouteSectionsEngine  —  Driver Routes › Route Sections (working page)

   Drop one or more Scanner Audit .xlsx files (one per day). The engine:
     1. parses each workbook client-side (no upload to a server),
     2. pulls a clean street out of every delivery/attempt address,
     3. snaps streets to the driver's registry route list,
     4. aggregates across all days (median delivery time per street),
     5. nominates 3 sections, flags anything that needs attention,
     6. lets you hand-adjust (session only — nothing writes back).

   All parsing/analysis lives in pure functions at the top so it can be tested
   outside React (it has been, against a real audit export).
   ========================================================================== */

/* ---------- street extraction ------------------------------------------- */
const TYPE_MAP = {
  ST: "STREET", STREET: "STREET", STR: "STREET",
  RD: "ROAD", ROAD: "ROAD",
  AV: "AVENUE", AVE: "AVENUE", AVENUE: "AVENUE",
  LN: "LANE", LANE: "LANE",
  PL: "PLACE", PLACE: "PLACE",
  // CREST is this team's own shorthand for Crescent on the route lists ("Elizabeth Bay
  // Crest" is Elizabeth Bay Crescent), so it has to map through or the street never
  // matches an AP scan. Safe here: no street actually ending in "Crest" exists in any
  // contract area — checked against the map.
  CR: "CRESCENT", CRES: "CRESCENT", CRESCENT: "CRESCENT", CREST: "CRESCENT",
  DR: "DRIVE", DRIVE: "DRIVE",
  PDE: "PARADE", PARADE: "PARADE",
  TCE: "TERRACE", TERRACE: "TERRACE",
  GDN: "GARDENS", GDNS: "GARDENS", GARDENS: "GARDENS", GARDEN: "GARDENS",
  CT: "COURT", COURT: "COURT", CL: "CLOSE", CLOSE: "CLOSE",
  WAY: "WAY", QUAY: "QUAY", HWY: "HIGHWAY", HIGHWAY: "HIGHWAY",
  WALK: "WALK", MALL: "MALL", ESP: "ESPLANADE", ESPLANADE: "ESPLANADE",
  BLD: "BOULEVARD", BLVD: "BOULEVARD", BVD: "BOULEVARD", BOULEVARD: "BOULEVARD", BOULEVARDE: "BOULEVARD",
};
const TYPES = new Set(Object.keys(TYPE_MAP));
const UNIT_RE = /^(U|UNIT|APT|APARTMENT|L|LVL|LEVEL|SHOP|SUITE|STE|FLAT|RM|ROOM|PO|GPO|G|LOT|TOWER)$/i;

const clean = (s) =>
  (s == null ? "" : String(s)).replace(/,/g, " ").replace(/\s+/g, " ").trim().toUpperCase();

function extractStreet(e, f) {
  for (const cand of [e, f]) {
    const c = clean(cand);
    if (!c || c === "#" || c === "-") continue;
    const toks = c.split(" ");
    for (let i = toks.length - 1; i >= 1; i--) {
      const t = toks[i].replace(/[^A-Z]/g, "");
      if (TYPES.has(t)) {
        const name = [];
        for (let j = i - 1; j >= 0; j--) {
          const w = toks[j];
          if (/^[0-9]+[A-Z]?$/.test(w)) break;            // house number
          if (/\d/.test(w) && j < i - 1) break;           // embedded number further back
          if (UNIT_RE.test(w)) break;
          const ww = w.replace(/[^A-Z]/g, "");
          if (ww) name.unshift(ww);
        }
        const filtered = name.filter((n) => n && !UNIT_RE.test(n));
        if (filtered.length) return filtered.join(" ") + " " + TYPE_MAP[t];
      }
    }
  }
  return null;
}

/* leading street number (the one right before the street name) */
function streetNumber(e, f, coreName) {
  const key = coreName.split(" ")[0]; // e.g. VICTORIA
  for (const cand of [e, f]) {
    const c = clean(cand);
    /* The (?:^|[^\d]) guard matters: without it \d{1,4} matches the TAIL of a longer
       run, so "309315 George Street" read as number 9315 and "Parcel Collect 10096
       43510" as 3510 \u2014 about 29% of scans landed on an invented house number, which then
       fed the out-of-range flags and the pass boundaries. A run of more than four digits
       isn't a house number: match nothing and let the caller treat it as unknown.
       (Written as a group rather than lookbehind, which older Safari throws on \u2014 and this
       regex is built at runtime, so it would throw in the user's face, not at build.) */
    const m = new RegExp(`(?:^|[^\\d])(\\d{1,4})(?:[-\u2013]\\d{1,4})?\\s+${key}\\b`).exec(c);
    if (m) return parseInt(m[1], 10);
  }
  return null;
}

/* ---------- time / date cells ------------------------------------------- */
function cellToSeconds(v) {
  if (v == null) return null;
  if (v instanceof Date) return v.getHours() * 3600 + v.getMinutes() * 60 + v.getSeconds();
  if (typeof v === "number") return Math.round((v % 1) * 86400);
  if (typeof v === "string") {
    const m = /(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?/i.exec(v);
    if (m) {
      let h = +m[1]; const mi = +m[2]; const s = m[3] ? +m[3] : 0;
      if (m[4]) { const pm = /pm/i.test(m[4]); if (h === 12) h = pm ? 12 : 0; else if (pm) h += 12; }
      return h * 3600 + mi * 60 + s;
    }
  }
  return null;
}
function cellToDayKey(v) {
  // Use LOCAL date parts — the library builds these Date cells at local midnight, so
  // toISOString() (UTC) would shift the day back one in +ve timezones (e.g. Sydney).
  if (v instanceof Date) return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}-${String(v.getDate()).padStart(2, "0")}`;
  if (typeof v === "number") return new Date(Math.round((v - 25569) * 86400000)).toISOString().slice(0, 10);
  if (typeof v === "string") return v;
  return "d";
}
function secsToStr(secs) {
  let h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60);
  const ap = h < 12 ? "am" : "pm"; const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}
function toMinutes(t) {
  const m = /(\d{1,2}):(\d{2})\s*(am|pm)/i.exec(t || "");
  if (!m) return 0;
  let h = +m[1]; const min = +m[2]; const pm = /pm/i.test(m[3]);
  if (h === 12) h = pm ? 12 : 0; else if (pm) h += 12;
  return h * 60 + min;
}

/* ---------- driver registry (from Driver_Routes) ------------------------ */
// label shown to the sorter; `match` = canonical key; ranges/shared for split streets.
const DRIVER_REGISTRY = {
  CHENZ19: {
    bays: "315", chute: "6", contract: "N22000",
    routes: [
      { match: "CHALLIS AVENUE", label: "Challis Avenue" },
      { match: "EARL PLACE", label: "Earl Place" },
      { match: "EARL STREET", label: "Earl Street" },
      { match: "HUGHES STREET", label: "Hughes Street" },
      { match: "LLANKELLY PLACE", label: "Llankelly Place" },
      { match: "MANNING STREET", label: "Manning Street" },
      { match: "MCDONALD STREET", label: "McDonald Street" },
      { match: "ORWELL STREET", label: "Orwell Street" },
      { match: "ROCKWALL CRESCENT", label: "Rockwall Cres" },
      { match: "SPRINGFIELD AVENUE", label: "Springfield Avenue" },
      { match: "TUSCULUM STREET", label: "Tusculum Street" },
      {
        match: "VICTORIA STREET",
        label: 'Victoria Street 1-207, 2-226 "Potts Point"',
        ranges: [{ lo: 1, hi: 207, parity: 1 }, { lo: 2, hi: 226, parity: 0 }],
        shared: 3,
      },
    ],
  },
};
// confirmed label fixes that merge silently (you approved Victoria Road → Street)
const CONFIRMED_ALIASES = { "VICTORIA ROAD": "VICTORIA STREET" };

/* Build the registry live from the Driver_Routes tab so every driver is covered.
   `rows` = array-of-arrays from that sheet (header:1), columns:
     0 ADAM  1 Login  2 Area  3 D#  4 Chute  5 Bay  6 Wave  7 Outlet  8 Streets JSON
   In the engine: buildRegistryFromRoutes(driverRoutesRows, { areaContract:{ DARL:"N22000" } })
   then pass the result as the `registry` prop. Shared-street counts (e.g. Victoria
   = 3 drivers) are derived automatically by tallying matches across all drivers. */
export function canonStreetName(label) {
  const s = clean(String(label).replace(/"[^"]*"/g, " ").replace(/\d+\s*[-\u2013]\s*\d+/g, " ").replace(/\d+/g, " "));
  const toks = s.split(" ").filter(Boolean);
  /* Take the FIRST street type, not the last. A route label LEADS with the street and
     then trails prose \u2014 "Holt Street Only 2 Holt St going to 14" \u2014 so scanning from the
     end (which is right for an ADDRESS, where the type comes last) finds the "St" in the
     prose and swallows everything back to the start: "HOLT STREET ONLY HOLT STREET",
     which can never match AP's "HOLT STREET". The street was quietly disowned and its
     parcels fell off every plan.
     Starting at 1 also keeps a leading "St" as Saint, not Street \u2014 "St James Ave". */
  for (let i = 1; i < toks.length; i++) {
    const t = toks[i].replace(/[^A-Z]/g, "");
    if (!TYPES.has(t)) continue;
    const name = [];
    for (let j = i - 1; j >= 0; j--) {
      const w = toks[j].replace(/[^A-Z]/g, "");
      if (!w || UNIT_RE.test(w)) break;
      name.unshift(w);
    }
    if (name.length) return name.join(" ") + " " + TYPE_MAP[t];
  }
  return s;
}
function parseRanges(label) {
  const out = []; const re = /(\d{1,4})\s*[-\u2013]\s*(\d{1,4})/g; let m;
  while ((m = re.exec(label))) out.push({ lo: +m[1], hi: +m[2], parity: +m[1] % 2 });
  return out.length ? out : null;
}
function routeFromLabel(label) {
  const r = { match: canonStreetName(label), label: String(label).trim() };
  const ranges = parseRanges(label);
  if (ranges) r.ranges = ranges;
  return r;
}
export function buildRegistryFromRoutes(rows, opts = {}) {
  const areaContract = opts.areaContract || {};
  const reg = {};
  for (const r of rows || []) {
    const login = r[1];
    if (!login || login === "Driver Login") continue;
    let streets = [];
    try { streets = r[8] ? JSON.parse(r[8]) : []; } catch (e) { streets = []; }
    reg[login] = {
      adam: r[0] != null ? String(r[0]) : null,
      bays: r[5] != null ? String(r[5]) : null,
      chute: r[4] != null ? String(r[4]) : null,
      dNumber: r[3] != null ? String(r[3]) : null,
      outlet: r[7] != null ? String(r[7]) : null,
      wave: r[6] != null ? String(r[6]) : null,
      area: r[2] || null,
      contract: areaContract[r[2]] || null,
      routes: streets.filter((s) => typeof s === "string" && s !== "—").map(routeFromLabel),
    };
  }
  /* A street is shared when more than one DRIVER has it — count drivers, not route
     entries. One driver routinely lists a street several times to carry its number
     ranges ("Collins Street 39-53" + "Collins Street 2-18, 1-31" + "Collins Street"),
     and 105 such cases exist live; tallying entries called each of them a shared street
     with 3 drivers on it. */
  const tally = {};
  Object.entries(reg).forEach(([login, e]) => e.routes.forEach((rt) => {
    (tally[rt.match] = tally[rt.match] || new Set()).add(login);
  }));
  Object.values(reg).forEach((e) => e.routes.forEach((rt) => {
    const n = tally[rt.match].size;
    if (n > 1) rt.shared = n;
  }));
  return reg;
}

const SECTION_META = {
  1: { code: "A", label: "Section A", tag: "First out", color: "#1B9C84" },
  2: { code: "B", label: "Section B", tag: "Mid-morning", color: "#D69A2D" },
  3: { code: "C", label: "Section C", tag: "Afternoon", color: "#C0563E" },
  4: { code: "D", label: "Section D", tag: "Last run", color: "#5667C6" },
};
export const SECTION_NUMS = [1, 2, 3, 4];
export const SECTION_COUNT = SECTION_NUMS.length;
export const sectionCode = (n) => (SECTION_META[n] ? SECTION_META[n].code : "");
/* Group a nominated list into {1:[],2:[],3:[],4:[]} — use this rather than a
   literal, so the section count lives in one place. */
export function groupSections(nominated) {
  const g = {};
  SECTION_NUMS.forEach((n) => (g[n] = []));
  nominated.forEach((s) => { if (g[s.section]) g[s.section].push(s); });
  return g;
}
const DELIVERY = (ev) =>
  ev === "Delivered" || ev === "Delivered - Left in a safe place" || /^Attempted delivery/.test(ev || "");

/* ---------- workbook parsing -------------------------------------------- */
export function parseAuditWorkbook(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null, blankrows: false });
  let contract = null, headerIdx = -1, header = null;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i] || [];
    if (r[0] === "Contract ID" && r[1]) contract = String(r[1]).trim(); // old: metadata row above the table
    if (r[0] === "Article ID") { headerIdx = i; header = r; break; }
  }
  if (headerIdx < 0 || !header) return { contract, events: [], drivers: [] };

  // Map columns by HEADER NAME so an inserted/re-ordered column (e.g. AP added a
  // "Contract ID" column, shifting everything right by one) can't break the read.
  const col = (...names) => {
    for (const n of names) {
      const i = header.findIndex((h) => String(h == null ? "" : h).trim().toLowerCase() === n.toLowerCase());
      if (i >= 0) return i;
    }
    return -1;
  };
  const cDriver = col("Driver ID") >= 0 ? col("Driver ID") : 1;
  const cAddr1 = col("Delivery Address Line 1") >= 0 ? col("Delivery Address Line 1") : 4;
  const cAddr2 = col("Delivery Address Line 2") >= 0 ? col("Delivery Address Line 2") : cAddr1 + 1;
  const cSuburb = col("Delivery Suburb") >= 0 ? col("Delivery Suburb") : cAddr1 + 2;
  const cDate = col("Date") >= 0 ? col("Date") : 8;
  const cTime = col("Time") >= 0 ? col("Time") : 9;
  const cContract = col("Contract ID"); // new layout carries it as a column
  // Event column: labelled "Event Code"/"Event" in the new layout; unlabelled (one
  // before Address Line 1) in the old layout.
  let cEvent = col("Event Code", "Event");
  if (cEvent < 0) cEvent = cAddr1 > 0 ? cAddr1 - 1 : 3;

  const events = [];
  const drivers = new Set();
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i]; if (!r) continue;
    const driver = r[cDriver]; if (!driver) continue;
    drivers.add(driver);
    if (contract == null && cContract >= 0 && r[cContract]) contract = String(r[cContract]).trim();
    const ev = r[cEvent];
    if (!DELIVERY(ev)) continue;
    const secs = cellToSeconds(r[cTime]);
    if (secs == null) continue;
    events.push({
      driver, e: r[cAddr1], f: r[cAddr2], suburb: clean(r[cSuburb]) === "#" ? "" : (r[cSuburb] || ""),
      secs, day: cellToDayKey(r[cDate]),
    });
  }
  return { contract, events, drivers: [...drivers].sort() };
}

/* ---------- build one driver's plan ------------------------------------- */
function snap(raw, routes) {
  if (CONFIRMED_ALIASES[raw]) raw = CONFIRMED_ALIASES[raw];
  const hit = routes.find((r) => r.match === raw);
  return { key: raw, label: hit ? hit.label : titleCase(raw), onList: !!hit, route: hit || null };
}

/* A driver works a long street in more than one go: part of it, off to another
   street, back for the rest. One row per street hides that — the run order reads
   as a single visit at a median time that never happened. So a street takes a
   PASS every time the driver leaves it and returns, and can appear at several
   places in the order (Smith St at 1, 5 and 8).

   There's no clock in this — a pause lasts however long it lasts. What ends a pass
   is the driver going and delivering ANOTHER STREET, and the catch is what counts as
   that. The scans interleave constantly: working down Barcom Ave he scans the odd
   cross-street address as he goes, sometimes seconds apart (a Mclachlan Ave parcel
   landed 10 seconds between two Barcom scans). Treating every one of those as an exit
   turns one street into eleven passes. He hasn't left; he's still walking down it.

   So a pass ends when the work done elsewhere is a real block — MIN_AWAY_WORK parcels
   on other streets — not a stray scan. On the day above that's exactly right: Barcom
   until 12:23, a solid block of Boundary Street, back to Barcom at 12:59. Two passes.

   Don't be tempted to use away-time for this instead. Across a real week it doesn't
   discriminate: the parcels delivered on return sit at ~4 whether the driver was gone
   two minutes or two hours. The size of the work does discriminate. */
const MIN_AWAY_WORK = 3; // parcels delivered elsewhere before a return counts as a new pass
const MIN_PASS = 5;      // parcels/day — under this it's a couple of drops, not a street worked in two goes

const medianInt = (xs) => {
  if (!xs.length) return null;
  const a = [...xs].sort((x, y) => x - y);
  return a[Math.floor(a.length / 2)];
};

/* One day's scans → Map(streetKey → array of passes, each pass = events in time order). */
function passesForDay(dayEvents, minAwayWork) {
  const evs = [...dayEvents].sort((a, b) => a.secs - b.secs);
  const runs = [];
  for (const ev of evs) {
    const last = runs[runs.length - 1];
    if (last && last.key === ev.key) last.events.push(ev);
    else runs.push({ key: ev.key, events: [ev] });
  }
  const byKey = new Map();
  const awayWork = new Map(); // street → parcels delivered elsewhere since we last left it
  for (const r of runs) {
    const list = byKey.get(r.key) || [];
    const away = awayWork.get(r.key) || 0;
    if (list.length && away < minAwayWork) list[list.length - 1].events.push(...r.events); // stray scan, never really left
    else list.push({ key: r.key, events: [...r.events] });
    byKey.set(r.key, list);
    for (const k of byKey.keys()) awayWork.set(k, k === r.key ? 0 : (awayWork.get(k) || 0) + r.events.length);
  }
  return byKey;
}

/* Bring one day's passes over a street down to `p` of them, by repeatedly folding the
   smallest pass into whichever neighbour it sits closest to in time. Days vary — the
   driver might touch a street four times on Monday and twice on Tuesday — so this is
   how a day is squared up against the street's standing pass count. */
function reduceToPasses(list, p) {
  const l = list.map((v) => ({ key: v.key, events: [...v.events] }));
  while (l.length > p) {
    let si = 0;
    for (let i = 1; i < l.length; i++) if (l[i].events.length < l[si].events.length) si = i;
    const endOf = (v) => v.events[v.events.length - 1].secs;
    const gapPrev = si > 0 ? l[si].events[0].secs - endOf(l[si - 1]) : Infinity;
    const gapNext = si < l.length - 1 ? l[si + 1].events[0].secs - endOf(l[si]) : Infinity;
    const into = gapPrev <= gapNext ? l[si - 1] : l[si + 1];
    into.events = [...into.events, ...l[si].events].sort((a, b) => a.secs - b.secs);
    l.splice(si, 1);
  }
  return l;
}

export function buildPlan(events, driver, regEntry, opts = {}) {
  const perDay = opts.perDay !== false; // default per-day average; pass {perDay:false} for collective totals
  const minPass = opts.minPass != null ? opts.minPass : MIN_PASS;
  const minAwayWork = opts.minAwayWork != null ? opts.minAwayWork : MIN_AWAY_WORK;
  // Reviewer's "same place" calls, keyed the same way as CONFIRMED_ALIASES — one door
  // with two street names collapses to a single stop in the run order.
  const aliases = { ...CONFIRMED_ALIASES, ...(opts.aliases || {}) };
  const drop = opts.dropStreets instanceof Set ? opts.dropStreets : new Set(opts.dropStreets || []);
  // Reviewer's corrections to malformed addresses, keyed on the address text: a rewrite
  // in addrEdits, or the address struck out entirely in dropAddrs.
  const addrEdits = opts.addrEdits || {};
  const dropAddrs = opts.dropAddrs instanceof Set ? opts.dropAddrs : new Set(opts.dropAddrs || []);
  const routes = (regEntry && regEntry.routes) || [];
  const rows = events.filter((e) => e.driver === driver);
  const days = new Set(rows.map((e) => e.day));
  const nDays = days.size || 1;

  // 1. bucket the scans by day, resolving each to its street key up front
  const byDay = new Map();
  for (const e of rows) {
    const id = addressId(e);
    if (dropAddrs.has(id)) continue;                      // struck out as unusable
    // A correction is the whole address, so it replaces both lines — otherwise the old
    // line 2 could still hand back the street the reviewer just fixed.
    const fix = addrEdits[id];
    const line1 = fix != null ? fix : e.e;
    const line2 = fix != null ? "" : e.f;
    const raw = extractStreet(line1, line2);
    if (!raw) continue;
    const key = aliases[raw] || raw;
    if (drop.has(key)) continue; // reviewer called this a mis-delivery — not this driver's work
    if (!byDay.has(e.day)) byDay.set(e.day, []);
    // carry the corrected lines forward — the house number is read off them further down
    byDay.get(e.day).push({ ...e, e: line1, f: line2, key });
  }

  // 2. cut each day into passes, and note how many passes each street took
  const dayPasses = new Map();
  const passCounts = new Map();
  const streetKeys = new Set();
  for (const [day, evs] of byDay) {
    const byKey = passesForDay(evs, minAwayWork);
    dayPasses.set(day, byKey);
    for (const [k, list] of byKey) {
      streetKeys.add(k);
      if (!passCounts.has(k)) passCounts.set(k, []);
      passCounts.get(k).push(list.length);
    }
  }

  // 3. settle how many passes each street gets in the standing plan.
  // Start from the median across the days — one odd day where the driver doubled
  // back shouldn't rewrite the run order — then shrink the count while any pass is
  // too small to be worth its own stop. Shrinking (rather than throwing the split
  // away wholesale) keeps a genuine 29+5 split that happens to carry a stray extra
  // pass, instead of dumping all 34 back into one row.
  const finalPasses = new Map();
  for (const k of streetKeys) {
    let p = medianInt(passCounts.get(k)) || 1;
    while (p > 1) {
      const per = new Array(p).fill(0);
      for (const byKey of dayPasses.values()) {
        const list = byKey.get(k);
        if (!list) continue;
        reduceToPasses(list, p).forEach((v, i) => (per[i] += v.events.length));
      }
      if (per.every((n) => n / nDays >= minPass)) break;
      p -= 1;
    }
    finalPasses.set(k, p);
  }

  // 4. aggregate across the week by (street, pass)
  const byVisit = new Map();
  for (const byKey of dayPasses.values()) {
    for (const [k, rawList] of byKey) {
      const maxPass = finalPasses.get(k) || 1;
      reduceToPasses(rawList, maxPass).forEach((pass, i) => {
        const p = i + 1;
        const id = k + "#" + p;
        if (!byVisit.has(id)) {
          const s = snap(k, routes);
          byVisit.set(id, { ...s, id, pass: p, passes: maxPass, times: [], suburb: "", out: 0, nums: [], starts: [], stops: [] });
        }
        const o = byVisit.get(id);
        const seen = [];
        for (const ev of pass.events) {
          o.times.push(ev.secs);
          if (ev.suburb) o.suburb = titleCase(clean(ev.suburb));
          const n = streetNumber(ev.e, ev.f, k);
          if (n != null) {
            o.nums.push(n); // keep the actual house numbers so a range-split street can be reviewed/assigned
            seen.push(n);
            if (o.route && o.route.ranges) {
              const inRange = o.route.ranges.some(
                (rg) => n >= rg.lo && n <= rg.hi && (rg.parity == null || n % 2 === rg.parity)
              );
              if (!inRange) o.out += 1;
            }
          }
        }
        // Where the pass began and where it broke off, in delivery order (not numeric
        // order — a driver may be running the odds down). Median across the days.
        if (seen.length) { o.starts.push(seen[0]); o.stops.push(seen[seen.length - 1]); }
      });
    }
  }

  const streets = [];
  for (const o of byVisit.values()) {
    const times = [...o.times].sort((a, b) => a - b);
    const med = times[Math.floor(times.length / 2)];
    streets.push({
      id: o.id, street: o.label, key: o.key, suburb: o.suburb, onList: o.onList,
      shared: o.route && o.route.shared ? o.route.shared : 0,
      outOfRange: o.out,
      numbers: [...new Set(o.nums)].sort((a, b) => a - b),
      pass: o.pass, passes: o.passes,
      stops: o.stops, starts: o.starts, // raw per-day, so the boundary can be judged below
      startNum: null, stopNum: null, boundaryVaries: false,
      parcels: perDay ? Math.max(1, Math.round(o.times.length / nDays)) : o.times.length,
      total: o.times.length,
      first: secsToStr(times[0]), last: secsToStr(times[times.length - 1]),
      median: secsToStr(med),
    });
  }
  streets.sort((a, b) => toMinutes(a.median) - toMinutes(b.median));
  resolveBoundaries(streets);
  return { streets, nDays };
}

/* Decide, per split street, whether "pauses at N" is a fact or a fiction.

   The only thing that makes a pause point worth printing is whether AP can SORT on it:
   is there a house number where everything below went in the first pass and everything
   above went in the second? So test exactly that — the two passes' number sets must not
   overlap across the whole week. Any boundary in the gap between them then holds for
   every day, even if the exact break-off wanders within it.

   Plenty of streets fail, and it's not noise — it's that they aren't split by number at
   all. William St's passes both run 1-100ish; Darlinghurst Rd stops at 82 and resumes at
   18. Those drivers aren't doing first-half-then-second-half, they're doing one side of
   the road then the other. No number separates them, and no map would help: it's about
   which side he walks, not where the corner is.

   Fail, and the street still shows two passes — that part is real and observed — but says
   the boundary varies instead of inventing a number for the sheet AP sorts to. */
function resolveBoundaries(streets) {
  const byKey = new Map();
  for (const s of streets) {
    if (s.passes < 2) continue;
    if (!byKey.has(s.key)) byKey.set(s.key, []);
    byKey.get(s.key).push(s);
  }
  for (const list of byKey.values()) {
    list.sort((a, b) => a.pass - b.pass);
    for (let i = 0; i < list.length - 1; i++) {
      const a = list[i], b = list[i + 1];
      const aMax = a.numbers.length ? a.numbers[a.numbers.length - 1] : null; // numbers[] is sorted
      const bMin = b.numbers.length ? b.numbers[0] : null;
      if (aMax != null && bMin != null && aMax < bMin) { a.stopNum = aMax; b.startNum = bMin; }
      else { a.boundaryVaries = b.boundaryVaries = true; }
    }
  }
}

/* ---------- nomination + flags ------------------------------------------ */
/* Split the run into SECTION_COUNT sections of even parcel load.

   The sections have to stay CONTIGUOUS in delivery order — a section is a bag the
   driver works through in one go, so it can't be a scatter of streets from across
   the day. That makes this the classic linear-partition problem: with the order
   fixed, the only choice is where to cut. Cutting at fixed k/N fractions of the
   cumulative load (what this used to do) lands wherever a street boundary happens
   to fall, which can leave one section carrying half again what its neighbour does.

   Instead, pick the cuts that minimise the summed squared deviation from
   total/count. An exact DP — squared error (not min-max) so it evens out all four
   sections rather than only shaving the heaviest. The list is ~30-60 streets and
   count is 4, so the O(n²k) table is nothing. */
export function nominateSections(streets, opts = {}) {
  const count = opts.count || SECTION_COUNT;
  const ordered = [...streets].sort((a, b) => toMinutes(a.median) - toMinutes(b.median));
  const n = ordered.length;
  if (!n) return [];
  const k = Math.min(count, n); // fewer streets than sections — don't invent empty ones
  const pre = [0];
  for (let i = 0; i < n; i++) pre.push(pre[i] + ordered[i].parcels);
  const target = pre[n] / k;
  const cost = (i, j) => { const d = pre[j] - pre[i] - target; return d * d; }; // streets i..j-1

  const dp = Array.from({ length: n + 1 }, () => new Array(k + 1).fill(Infinity));
  const cut = Array.from({ length: n + 1 }, () => new Array(k + 1).fill(-1));
  dp[0][0] = 0;
  for (let s = 1; s <= k; s++) {
    // leave at least one street for each of the k-s sections still to come
    for (let j = s; j <= n - (k - s); j++) {
      for (let i = s - 1; i < j; i++) {
        if (dp[i][s - 1] === Infinity) continue;
        const c = dp[i][s - 1] + cost(i, j);
        if (c < dp[j][s]) { dp[j][s] = c; cut[j][s] = i; }
      }
    }
  }
  const out = ordered.map((s) => ({ ...s }));
  let j = n;
  for (let s = k; s >= 1; s--) {
    const i = cut[j][s];
    for (let x = i; x < j; x++) out[x].section = s;
    j = i;
  }
  return withDeliveryOrder(out);
}

/* Addresses whose house number can't be real.

   No street in these contract areas reaches four figures — Bourke Street tops out at 729
   — so a number past MAX_HOUSE means the address is malformed, not that the driver went
   somewhere unusual. It's the customer running an apartment number into the street number
   with nothing between them:

     "UNIT 19 2022 SPRINGFIELD AVENUE"  → 20-22 Springfield Avenue, unit 19
     "6070 WILLIAM ST UNIT 8"           → 60-70 William Street, unit 8
     "MEZZANINE 8892 COOPER STREET"     → 88-92 Cooper Street

   Don't guess the split. "8151 Castlereagh St" could be 81/51 or 81-51 and only a person
   can say — so list them and let one fix or drop each. There are ~30 across a week, which
   is a list you can read; a threshold that catches more would be catching real numbers
   (501 Glebe Point Road and 438 Victoria Street are genuine).

   Keyed on BOTH address lines, so one correction fixes every scan carrying that address
   and only those. Line 1 alone won't do: one of these reads just "Apt 108" with the
   street on line 2, and keying on that would rewrite every unrelated parcel that happens
   to share it. */
const MAX_HOUSE = 1000;
export const addressId = (e) => `${clean(e.e)} | ${clean(e.f)}`;

export function detectBadAddresses(events, driver, opts = {}) {
  const max = opts.max != null ? opts.max : MAX_HOUSE;
  const rows = events.filter((e) => e.driver === driver);
  const by = new Map();
  for (const e of rows) {
    const raw = extractStreet(e.e, e.f);
    if (!raw) continue;
    const key = CONFIRMED_ALIASES[raw] || raw;
    const n = streetNumber(e.e, e.f, key);
    if (n == null || n <= max) continue;
    const id = addressId(e);
    let o = by.get(id);
    if (!o) by.set(id, (o = {
      id, street: titleCase(key), num: n, count: 0, days: new Set(),
      // what the reviewer reads and edits: both lines, minus the "#" AP uses for empty
      raw: [clean(e.e), clean(e.f)].filter((s) => s && s !== "#").map(titleCase).join(" · "),
      edit: titleCase(clean(e.e)),
    }));
    o.count += 1;
    o.days.add(e.day);
  }
  return [...by.values()].map((o) => ({ ...o, nDays: o.days.size })).sort((a, b) => b.count - a.count || b.num - a.num);
}

/* Two different streets scanned within seconds of each other.

   The driver cannot have moved, so both parcels went to the same spot. That's one of
   three things, and only a person can tell them apart:
     · one place with two names — a corner building fronting both streets
       ("316/105 Campbell Street" and "105 Commonwealth Street" are one door)
     · a mis-delivery — a parcel handed to the wrong street entirely
     · AP's own address junk — "20 Pelican St" arriving as "…415 / 20 PEL" and parsing
       to a street called ICAN

   Don't try to be clever about which. The obvious test — "does this pair repeat across
   the week?" — fails, because corner addresses barely get parcels: only 2 of 62 pairs
   repeated on 3+ days. So show every occurrence and let the reviewer say.

   CLOSE_SCAN is generous at 10s: a real walk to another street can't happen in that,
   and AP's timestamps are only second-resolution. */
const CLOSE_SCAN = 10; // seconds

export function detectCloseScans(events, driver, opts = {}) {
  const gap = opts.gap != null ? opts.gap : CLOSE_SCAN;
  const rows = events.filter((e) => e.driver === driver);
  const byDay = new Map();
  for (const e of rows) {
    const raw = extractStreet(e.e, e.f);
    if (!raw) continue;
    const key = CONFIRMED_ALIASES[raw] || raw;
    if (!byDay.has(e.day)) byDay.set(e.day, []);
    byDay.get(e.day).push({ ...e, key });
  }
  const pairs = new Map();
  for (const [day, evs] of byDay) {
    evs.sort((a, b) => a.secs - b.secs);
    for (let i = 1; i < evs.length; i++) {
      const a = evs[i - 1], b = evs[i];
      if (a.key === b.key) continue;
      const dt = b.secs - a.secs;
      if (dt > gap) continue;
      // order-independent: A⇄B and B⇄A are the same place, not two findings
      const [x, y] = a.key < b.key ? [a, b] : [b, a];
      const id = `${x.key}|${y.key}`;
      let o = pairs.get(id);
      if (!o) pairs.set(id, (o = { id, a: x.key, b: y.key, streetA: titleCase(x.key), streetB: titleCase(y.key), hits: [], days: new Set() }));
      o.days.add(day);
      if (o.hits.length < 6) o.hits.push({ day, secs: dt, addrA: titleCase(clean(x.e)), addrB: titleCase(clean(y.e)) });
    }
  }
  return [...pairs.values()]
    .map((o) => ({ ...o, nDays: o.days.size, count: o.hits.length }))
    .sort((p, q) => q.nDays - p.nDays || q.count - p.count);
}

/* Streets nobody owns, across the whole loaded week — worst first.

   The per-driver flags bury this. "Kings Cross Road — not on ZHENGZ3's route list"
   reads exactly like the 1/day hospital loading docks sitting next to it, when in fact
   it's ~55 parcels a day on NO driver's list anywhere: work that never reaches the sort
   plan AP receives. Volume is what separates the two, so rank on it and say it once for
   the contract rather than once per driver. */
export function missingStreets(events, owners) {
  const nDays = new Set(events.map((e) => e.day)).size || 1;
  const by = new Map();
  for (const e of events) {
    const raw = extractStreet(e.e, e.f);
    if (!raw) continue;
    const key = CONFIRMED_ALIASES[raw] || raw;
    if (owners[canonStreetName(key)]) continue; // someone's route covers it
    let o = by.get(key);
    if (!o) by.set(key, (o = { key, street: titleCase(key), total: 0, byDriver: new Map() }));
    o.total += 1;
    o.byDriver.set(e.driver, (o.byDriver.get(e.driver) || 0) + 1);
  }
  return [...by.values()]
    .map((o) => {
      const drivers = [...o.byDriver.entries()].sort((a, b) => b[1] - a[1]).map(([driver, n]) => ({ driver, total: n }));
      return { ...o, drivers, top: drivers[0].driver, perDay: Math.round(o.total / nDays), nDays };
    })
    .sort((a, b) => b.total - a.total);
}

/* Number the streets in the order the driver actually drives them — 1..N straight
   through the run. Streets pulled out of the plan don't take a number. Call this
   again after any hand-move so the numbering stays gapless. */
export function withDeliveryOrder(list) {
  let n = 0;
  return list.map((s) => ({ ...s, order: s.section === 0 ? null : ++n }));
}
const fmtDur = (mins) => {
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m} min`;
};
const firstWord = (s) => (s || "").trim().split(/\s+/)[0].toUpperCase();

export function detectFlags(nominated, regEntry, sections, driver, owners) {
  const flags = [];
  const total = nominated.reduce((a, s) => a + (s.section === 0 ? 0 : s.parcels), 0) || 1;
  const routes = (regEntry && regEntry.routes) || [];
  const sole = new Set(SECTION_NUMS.filter((n) => sections[n] && sections[n].length === 1).map((n) => sections[n][0].street));
  // A split street is two rows now, but "not on the route list" is one problem with
  // it, not one per pass. Raise each street-level flag once, against its first pass.
  const said = new Set();
  const once = (street, kind) => { const k = street + "|" + kind; if (said.has(k)) return false; said.add(k); return true; };

  nominated.forEach((s, idx) => {
    if (s.section === 0) return; // pulled out of the plan — don't flag it
    if (routes.length && !s.onList) {
      if (!once(s.street, "offlist")) return;
      const alias = routes.find((r) => firstWord(r.label) === firstWord(s.street));
      const canon = owners ? canonStreetName(s.street) : null;
      const other = canon && owners && owners[canon] ? [...owners[canon]].filter((d) => d !== driver) : [];
      flags.push({
        sev: "review", ref: s.street, idx,
        msg: other.length
          ? `On ${other.join(", ")}'s route, not ${driver}'s (${s.parcels}/day) — wrong-driver scan or transfer.`
          : alias
          ? `Not on ${driver}'s route list — looks like “${alias.label.replace(/\s+\d.*$/, "")}”. Confirm before merging.`
          : `Not on ${driver}'s route list (${s.parcels}/day). Check for a wrong-driver scan or transfer.`,
      });
      return;
    }
    if (s.outOfRange > 0) {
      if (once(s.street, "range")) flags.push({
        sev: "review", ref: s.street, idx,
        msg: `${s.outOfRange} parcel${s.outOfRange > 1 ? "s" : ""} outside ${driver}'s number range on a shared street — verify they're his.`,
      });
    }
    const span = toMinutes(s.last) - toMinutes(s.first);
    // A wide span used to be the only hint that a street was worked in two goes.
    // Now that the passes are split out explicitly, it's only worth saying when
    // the street stayed a single pass and still stretched across the day.
    if (span >= 120 && s.passes === 1 && !sole.has(s.street) && once(s.street, "span")) {
      flags.push({ sev: "watch", ref: s.street, idx, msg: `Wide delivery span (${fmtDur(span)}) in a single pass — check the section fit.` });
    }
  });

  SECTION_NUMS.forEach((n) => {
    const grp = sections[n] || [];
    if (grp.length === 0) { flags.push({ sev: "review", ref: `Section ${sectionCode(n)}`, msg: "No streets — empty section." }); return; }
    const p = grp.reduce((a, s) => a + s.parcels, 0);
    const share = p / total;
    if (grp.length === 1 && share >= 0.3) {
      const only = grp[0]; const span = toMinutes(only.last) - toMinutes(only.first);
      const idx = nominated.findIndex((x) => x.id === only.id);
      flags.push({
        sev: "watch", ref: `Section ${sectionCode(n)}`, idx,
        msg: `Carried by one street — ${only.street.replace(/\s+\d.*$/, "")}, ${Math.round(share * 100)}% of the day${span >= 90 ? `, ${fmtDur(span)} span` : ""}. Low resilience; check it shouldn't split.`,
      });
    }
  });

  // The nomination always cuts sections out of the run in order, so a section whose
  // streets aren't consecutive can only come from a hand-move. Worth saying: AP reads
  // a section as one continuous stretch of the run, and this one no longer is.
  const inPlan = nominated.filter((s) => s.section !== 0).sort((a, b) => (a.order || 0) - (b.order || 0));
  SECTION_NUMS.forEach((n) => {
    const at = inPlan.map((s, i) => (s.section === n ? i : -1)).filter((i) => i >= 0);
    if (at.length > 1 && at[at.length - 1] - at[0] + 1 !== at.length) {
      flags.push({
        sev: "watch", ref: `Section ${sectionCode(n)}`,
        msg: `Not a continuous stretch of the run — its streets are split by other sections. A moved street is out of sequence.`,
      });
    }
  });

  const loads = SECTION_NUMS.map((n) => (sections[n] || []).reduce((a, s) => a + s.parcels, 0));
  if (loads.every((x) => x > 0)) {
    const mx = Math.max(...loads), mn = Math.min(...loads);
    if (mx >= 2 * mn) flags.push({ sev: "watch", ref: "Balance", msg: `Uneven load — heaviest section is ${(mx / mn).toFixed(1)}× the lightest.` });
  }
  const order = { review: 0, watch: 1 };
  return flags.sort((a, b) => order[a.sev] - order[b.sev]);
}

/* ---------- small format helpers ---------------------------------------- */
function titleCase(s) { return (s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()); }
function fmtWindow(min) {
  let h = Math.floor(min / 60); const m = min % 60;
  const ap = h < 12 ? "am" : "pm"; const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}
const AXIS_START = 8 * 60, AXIS_END = 13 * 60 + 30, AXIS_SPAN = AXIS_END - AXIS_START;
const pct = (min) => ((min - AXIS_START) / AXIS_SPAN) * 100;
const fmtAxis = (min) => { let h = Math.floor(min / 60); const ap = h < 12 ? "am" : "pm"; return `${h % 12 || 12}${ap}`; };

/* ---- the driver's OWN section names, from the Section Scan tab's data ----
   The audit .xlsx (streets) and the scanner .csv (section names) share no key,
   so we link them by time: each scanner section has a start/end window, and we
   tag a street by whichever section window contains its median delivery time. */
function loadScannerSections() {
  try {
    const raw = localStorage.getItem("hp_sectionScan_v1");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const events = parsed && parsed.events ? parsed.events : [];
    const byDriver = {};
    for (const e of events) {
      if (!e.driver || !e.section) continue;
      const d = byDriver[e.driver] || (byDriver[e.driver] = {});
      const st = toMinutes(e.secStart), en = toMinutes(e.secEnd);
      const s = d[e.section];
      if (!s) d[e.section] = { name: e.section, start: st || 0, end: en || 1440 };
      else { if (st && st < s.start) s.start = st; if (en && en > s.end) s.end = en; }
    }
    const out = {};
    Object.keys(byDriver).forEach((dv) => { out[dv] = Object.values(byDriver[dv]).sort((a, b) => a.start - b.start); });
    return out;
  } catch (e) { return {}; }
}
function sectionForTime(sections, min) {
  if (!sections || !sections.length || min == null) return "";
  const hit = sections.find((s) => min >= s.start && min <= s.end);
  if (hit) return hit.name;
  let best = null, bd = Infinity;
  for (const s of sections) { const mid = (s.start + s.end) / 2; const d = Math.abs(min - mid); if (d < bd) { bd = d; best = s; } }
  return best ? best.name : "";
}

/* day label for the day toggle (ISO yyyy-mm-dd -> "Mon 01 Jun", else raw) */
function dayLabelOf(key, idx) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(key || "");
  if (m) {
    const d = new Date(+m[1], +m[2] - 1, +m[3]);
    const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${WD[d.getDay()]} ${m[3]} ${MO[+m[2] - 1]}`;
  }
  return key || `Day ${(idx || 0) + 1}`;
}

/* ---------- route review: flag streets against the live route lists ------ */
function lev(a, b) {
  a = a || ""; b = b || "";
  const m = a.length, n = b.length; const d = [];
  for (let i = 0; i <= m; i++) d[i] = [i];
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++)
    d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[m][n];
}
export function buildOwners(registry) {
  const o = {};
  Object.keys(registry || {}).forEach((dv) => (registry[dv].routes || []).forEach((rt) => {
    if (rt.match) (o[rt.match] = o[rt.match] || new Set()).add(dv);
  }));
  return o;
}
// Classify one street for a driver against every driver's route list.
// flag: "" ok | UNNAMED | OTHER (on/looks-like another driver) | TYPO (own street) | OFFLIST
export function reviewStreet(street, driver, registry, owners) {
  const canon = canonStreetName(street);
  const raw = String(street || "").toLowerCase();
  const reg = registry[driver];
  if (canon && reg && (reg.routes || []).some((rt) => rt.match === canon)) return { flag: "", note: "" };
  if (/\b(loading|access via|cnr|corner|school|hospital|station|carpark|car park|rear|behind|opp)\b/.test(raw)
    || /\bonly\b|going to/.test(raw)
    || /(street|road|avenue|lane|place|terrace|drive|crescent|cres|parade|way)\s+(street|road|avenue|lane|place|terrace|drive|crescent|cres|parade|way)\b/.test(raw))
    return { flag: "UNNAMED", note: "Unclear / not a clean street name" };
  const own = owners[canon] ? [...owners[canon]].filter((d) => d !== driver) : [];
  if (own.length) return { flag: "OTHER", note: "On " + own.join(", ") + "’s route" };
  for (const rt of (reg && reg.routes) || []) if (rt.match && rt.match !== canon && lev(canon, rt.match) <= 2) return { flag: "TYPO", note: "Likely AP misspelling of this driver’s ‘" + rt.label + "’" };
  for (const od of Object.keys(registry)) { if (od === driver) continue; for (const rt of (registry[od].routes || [])) if (rt.match && lev(canon, rt.match) <= 2) return { flag: "OTHER", note: "Looks like " + od + "’s ‘" + rt.label + "’" }; }
  return { flag: "OFFLIST", note: "Not on any route — check (wrong area / new street)" };
}

/* ---------- retain the loaded week across sessions (browser-local) ------- */
const RSE_KEY = "hp_routeSections_v1";
const RSE_VER = 3; // bump to drop retained data parsed by older code (e.g. the UTC date bug)

/* ---------- dated project saves ------------------------------------------
   The live copy (RSE_KEY / the routeSections blob) is a single slot: whatever you're
   working on now. One click of Clear used to wipe it from the browser AND the server,
   and Netlify Blobs keeps no history, so a week's review evaporated. Reviewing a week is
   hours of judgement — it deserves to be saved and dated, not held in one overwritable
   slot.

   So: a save is the WHOLE project (the week + the section moves + the reviewed flags +
   the corner/address calls) under its own key, listed in an index blob. Keys go through
   user-store's clean() — [^A-Za-z0-9_] is stripped — so they're prefix + epoch ms and
   nothing else. */
const SNAP_PREFIX = "routeSave_";
const SNAP_INDEX = "routeSaves";
const MAX_SNAPS = 10; // oldest pruned; each is ~750KB compressed

const fmtSavedAt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (e) { return iso; }
};
/* "1–5 Jun" / "15 Jun" — name a save after the week it holds, not a filename. */
function weekLabel(parsed) {
  const days = [...new Set((parsed?.events || []).map((e) => e.day))].sort();
  if (!days.length) return "Empty week";
  const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const part = (k) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(k); return m ? { d: +m[3], mo: MO[+m[2] - 1] } : null; };
  const a = part(days[0]), b = part(days[days.length - 1]);
  if (!a || !b) return `${days.length} day${days.length > 1 ? "s" : ""}`;
  if (days.length === 1) return `${a.d} ${a.mo}`;
  return a.mo === b.mo ? `${a.d}–${b.d} ${a.mo}` : `${a.d} ${a.mo} – ${b.d} ${b.mo}`;
}
/* Section overrides saved before v3 can't be carried over: they name a section in the
   old three-section plan (where 3 meant "last run" — it means "afternoon" now), and
   they're keyed by street name, which no longer identifies a stop once a street can be
   worked in two passes. Re-pointing them would be guesswork, so they're dropped and the
   plan falls back to auto. The loaded week and the review acks are untouched. */
function migrateRSE(o) {
  const from = o.v || 1;
  const p = o.parsed || {};
  let events = p.events || [];
  if (from < 2) {
    // v1: the UTC date bug shifted audit day keys back one; shift them forward.
    const shiftDay = (k) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(k || "");
      if (!m) return k;
      const d = new Date(+m[1], +m[2] - 1, +m[3] + 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    events = events.map((e) => ({ ...e, day: shiftDay(e.day) }));
  }
  return {
    v: RSE_VER,
    parsed: { ...p, events },
    driver: o.driver,
    overridesByDriver: from < 3 ? {} : (o.overridesByDriver || {}),
    ackByDriver: o.ackByDriver || {},
  };
}
function loadRSE() {
  try {
    const s = localStorage.getItem(RSE_KEY);
    if (!s) return null;
    const o = JSON.parse(s);
    if (!o || !o.parsed) return null;
    if (o.v === RSE_VER) return o;
    const migrated = migrateRSE(o); // MIGRATE in place — never discard the user's week
    try { localStorage.setItem(RSE_KEY, JSON.stringify(migrated)); } catch (e2) {}
    return migrated;
  } catch (e) { return null; }
}
function saveRSE(parsed, driver, overridesByDriver, ackByDriver) {
  if (!parsed) { try { localStorage.removeItem(RSE_KEY); } catch (e) {} return; }
  try { localStorage.setItem(RSE_KEY, JSON.stringify({ v: RSE_VER, parsed, driver, overridesByDriver, ackByDriver })); return; }
  catch (e) {
    // over quota — keep at least the selected driver's view so it still returns
    try {
      const slim = { ...parsed, events: parsed.events.filter((ev) => ev.driver === driver), drivers: [driver] };
      localStorage.setItem(RSE_KEY, JSON.stringify({ v: RSE_VER, parsed: slim, driver, overridesByDriver, ackByDriver, slim: true }));
    } catch (e2) {}
  }
}

/* ======================================================================== */
export default function RouteSectionsEngine({ registry = DRIVER_REGISTRY, onParsed, focusDriver, login, onRepair }) {
  const [saved] = useState(loadRSE);
  const [parsed, setParsed] = useState(() => (saved ? saved.parsed : null)); // {contract, events, drivers}
  const [driver, setDriver] = useState(() => (saved ? saved.driver || "" : ""));
  const [ovByDriver, setOvByDriver] = useState(() => {
    if (!saved) return {};
    return saved.overridesByDriver || (saved.overrides && saved.driver ? { [saved.driver]: saved.overrides } : {});
  }); // section moves + exclusions, kept per driver so they survive a driver switch
  const [ackByDriver, setAckByDriver] = useState(() => (saved && saved.ackByDriver) || {}); // per-driver "reviewed" acks — clears a flag the delivery data can't
  const [closeByDriver, setCloseByDriver] = useState({}); // per-driver same-place / mis-delivery calls on scans seconds apart
  const [addrByDriver, setAddrByDriver] = useState({});   // per-driver fixes to malformed addresses: {addr: {to} | 'remove'}
  const [editingAddr, setEditingAddr] = useState(null);   // address id currently open in the editor
  const [editDraft, setEditDraft] = useState("");
  const [showAcked, setShowAcked] = useState(false);
  const [hover, setHover] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dayView, setDayView] = useState("all"); // "all" = collective total across days, else a day key
  const [fileDrag, setFileDrag] = useState(false);
  // Stored-audit loader: pull a date range straight from scan_events (the daily uploads
  // on the Scanner Audit page) instead of re-dropping the week's .xlsx files.
  const [storedTo, setStoredTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [storedFrom, setStoredFrom] = useState(() => new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10));
  const [exportScope, setExportScope] = useState("all"); // "all" | "area:DARL" | "driver:CHENZ19"
  const [addQ, setAddQ] = useState(""); // "Add a street" search box
  const [correcting, setCorrecting] = useState(null); // flag street currently being spell-corrected
  const [correctQ, setCorrectQ] = useState("");
  const [reviewingNums, setReviewingNums] = useState(null); // flag street whose house numbers are being assigned
  const [assignTo, setAssignTo] = useState(""); // manual target driver when a number's range can't be resolved
  const [addingRange, setAddingRange] = useState(null); // flag street being added to THIS driver with a number range
  const [rangeInput, setRangeInput] = useState("");
  const [cloud, setCloud] = useState(login ? "syncing" : "off"); // off | syncing | synced
  const cloudReady = useRef(false);
  const cloudTimer = useRef(null);
  const [snaps, setSnaps] = useState([]);        // dated project saves (index only — payloads load on demand)
  const [snapsOpen, setSnapsOpen] = useState(false);
  const [snapBusy, setSnapBusy] = useState("");  // "" | "saving" | "restoring"
  const [snapNote, setSnapNote] = useState("");

  // On mount, if a saved week was restored, hand it to the page so Driver Complete rebuilds too.
  useEffect(() => { if (saved && saved.parsed && onParsed) onParsed(saved.parsed); }, []);

  // The list of dated saves (index blob — small; the payloads are only fetched on restore).
  useEffect(() => {
    if (!login) return;
    let cancelled = false;
    cloudLoad(login, SNAP_INDEX).then((i) => { if (!cancelled) setSnaps(Array.isArray(i) ? i : []); });
    return () => { cancelled = true; };
  }, [login]);

  // Server-side retention: cloud is authoritative. On mount, pull the user's saved
  // week; if the cloud has it, use it; if the cloud is empty but this browser has a
  // local copy, push it up. Only after that do we start saving changes to the cloud.
  useEffect(() => {
    if (!login) return;
    let cancelled = false;
    (async () => {
      const c = await cloudLoad(login, "routeSections");
      if (cancelled) return;
      if (c && c.parsed && c.parsed.events && c.parsed.events.length) {
        const m = c.v === RSE_VER ? c : migrateRSE(c); // cloud copies predate the version stamp
        setParsed(m.parsed);
        setDriver(m.driver || (m.parsed.drivers && m.parsed.drivers[0]) || "");
        setOvByDriver(m.overridesByDriver || {});
        setAckByDriver(m.ackByDriver || {});
        if (onParsed) onParsed(m.parsed);
      } else if (saved && saved.parsed && saved.parsed.events && saved.parsed.events.length) {
        cloudSave(login, "routeSections", { v: RSE_VER, parsed: saved.parsed, driver: saved.driver, overridesByDriver: saved.overridesByDriver || {}, ackByDriver: saved.ackByDriver || {} });
      }
      cloudReady.current = true;
      if (!cancelled) setCloud("synced");
    })();
    return () => { cancelled = true; };
  }, [login]); // eslint-disable-line

  // Retain the loaded week locally (instant) + debounced to the cloud (durable).
  useEffect(() => {
    saveRSE(parsed, driver, ovByDriver, ackByDriver);
    if (login && cloudReady.current) {
      if (cloudTimer.current) clearTimeout(cloudTimer.current);
      cloudTimer.current = setTimeout(() => {
        cloudSave(login, "routeSections", parsed ? { v: RSE_VER, parsed, driver, overridesByDriver: ovByDriver, ackByDriver } : null);
      }, 1500);
    }
  }, [parsed, driver, ovByDriver, ackByDriver, login]);

  const moveCount = () => Object.values(ovByDriver).reduce((a, o) => a + Object.keys(o || {}).length, 0);
  const ackCount = () => Object.values(ackByDriver).reduce((a, o) => a + Object.keys(o || {}).length, 0);

  /* A save is the whole project — the week AND every judgement made on it. Re-dropping the
     audit files rebuilds the week, but nothing can rebuild the moves and reviews, so those
     are the part actually worth keeping. */
  const projectState = () => ({
    v: RSE_VER, parsed, driver,
    overridesByDriver: ovByDriver, ackByDriver,
    closeByDriver, addrByDriver,
  });

  const saveProject = async (note) => {
    if (!parsed || !login) return null;
    const ts = Date.now();
    const key = SNAP_PREFIX + ts;
    if (!(await cloudSave(login, key, projectState()))) return null;
    const entry = {
      key, savedAt: new Date(ts).toISOString(), note: note || "",
      label: weekLabel(parsed), driver,
      days: new Set((parsed.events || []).map((e) => e.day)).size,
      scans: (parsed.events || []).length,
      moves: moveCount(), acks: ackCount(),
    };
    const next = [entry, ...snaps];
    // Prune oldest beyond MAX_SNAPS — delete the payload, not just the index entry, or the
    // store fills with blobs nothing points at.
    for (const d of next.slice(MAX_SNAPS)) cloudSave(login, d.key, null);
    const kept = next.slice(0, MAX_SNAPS);
    await cloudSave(login, SNAP_INDEX, kept);
    setSnaps(kept);
    return entry;
  };

  const onSaveClick = async () => {
    if (!parsed) return;
    setSnapBusy("saving"); setSnapNote("");
    const e = await saveProject("");
    setSnapBusy("");
    setSnapNote(e ? `Saved — ${e.label}, ${fmtSavedAt(e.savedAt)}` : "Couldn't save. Check your connection and try again.");
    if (e) setSnapsOpen(true);
  };

  const restoreProject = async (entry) => {
    if (!login) return;
    if (typeof window !== "undefined" && window.confirm && parsed &&
        !window.confirm(`Open the save from ${fmtSavedAt(entry.savedAt)} (${entry.label})?\n\nWhat's on screen now will be replaced. Save it first if you want to keep it.`)) return;
    setSnapBusy("restoring"); setSnapNote("");
    const o = await cloudLoad(login, entry.key);
    if (!o || !o.parsed || !o.parsed.events) { setSnapBusy(""); setSnapNote("Couldn't read that save."); return; }
    const m = o.v === RSE_VER ? o : migrateRSE(o);
    setParsed(m.parsed);
    setDriver(m.driver || (m.parsed.drivers && m.parsed.drivers[0]) || "");
    setOvByDriver(m.overridesByDriver || {});
    setAckByDriver(m.ackByDriver || {});
    setCloseByDriver(o.closeByDriver || {});
    setAddrByDriver(o.addrByDriver || {});
    if (onParsed) onParsed(m.parsed);
    setSnapBusy(""); setSnapNote(`Opened — ${entry.label}, saved ${fmtSavedAt(entry.savedAt)}`);
  };

  /* Clear takes a snapshot FIRST, so it can't cost anything. It still wipes the live slot
     from this browser and the server (Netlify Blobs keeps no history, so that part really
     is gone), but the work lands in the saves list and is one click from coming back. */
  const clearSaved = async () => {
    const days = parsed ? new Set((parsed.events || []).map((e) => e.day)).size : 0;
    const lose = [
      days ? `${days} day${days > 1 ? "s" : ""} of loaded scans` : null,
      moveCount() ? `${moveCount()} section move${moveCount() > 1 ? "s" : ""}` : null,
      ackCount() ? `${ackCount()} reviewed flag${ackCount() > 1 ? "s" : ""}` : null,
    ].filter(Boolean);
    const msg = login
      ? `Clear the loaded week?\n\nThis removes ${lose.join(", ")} from the screen.\n\nIt will be saved first as "Before clear" — you can reopen it from Saves. Your route lists and the AP change log are not affected.`
      : `Clear the loaded week?\n\nThis removes ${lose.join(", ")} — and you're not signed in, so it CANNOT be saved first or undone.\n\nYour route lists and the AP change log are not affected.`;
    if (typeof window !== "undefined" && window.confirm && !window.confirm(msg)) return;
    if (login && parsed) { setSnapBusy("saving"); await saveProject("Before clear"); setSnapBusy(""); }
    setParsed(null); setDriver(""); setOvByDriver({}); setAckByDriver({});
    setCloseByDriver({}); setAddrByDriver({});
    if (onParsed) onParsed(null);
    if (login) cloudSave(login, "routeSections", null); // clear the live slot
    setSnapNote(login ? "Cleared — saved first as “Before clear”, reopen it from Saves." : "Cleared.");
  };

  const onFiles = useCallback(async (fileList) => {
    const files = [...fileList].filter((f) => /\.xlsx?$/i.test(f.name));
    if (!files.length) { setErr("Drop the Scanner Audit .xlsx file(s)."); return; }
    setBusy(true); setErr("");
    try {
      let contract = null; const fresh = [];
      for (const f of files) {
        const buf = await f.arrayBuffer();
        const r = parseAuditWorkbook(buf);
        contract = contract || r.contract;
        r.events.forEach((e) => fresh.push(e));
      }
      // Accumulate days across separate drops; a re-dropped day refreshes just that day.
      const freshDays = new Set(fresh.map((e) => e.day));
      const kept = (parsed && parsed.events ? parsed.events : []).filter((e) => !freshDays.has(e.day));
      const events = [...kept, ...fresh];
      const drivers = [...new Set(events.map((e) => e.driver))].sort();
      const days = new Set(events.map((e) => e.day)).size;
      const next = { contract: contract || (parsed && parsed.contract) || null, events, drivers, days };
      setParsed(next);
      setDriver((d) => (d && drivers.includes(d) ? d : (drivers.includes("CHENZ19") ? "CHENZ19" : drivers[0] || "")));
      // Keep any per-driver overrides — they're keyed by street so a re-drop won't lose them.
      // Lift the parsed week up so the Driver Complete tab builds its roster from the same events.
      if (onParsed) onParsed(next);
    } catch (e) { setErr("Couldn't read that file — is it the Scanner Audit export?"); }
    setBusy(false);
  }, [onParsed, parsed]);

  // Load a date range from the stored daily audits (scan_events) — same event shape as a
  // dropped file, so the plan builds identically. Accumulates like onFiles: days already
  // on screen from a file drop are kept; the fetched days refresh in place.
  const loadStored = useCallback(async (from, to) => {
    if (!from || !to) { setErr("Pick a from and to date."); return; }
    setBusy(true); setErr("");
    try {
      const res = await fetch("/.netlify/functions/scanner-audit-full-sb", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "routeEvents", from, to }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || "load failed");
      if (!j.events.length) { setErr(`No stored scans between ${from} and ${to}. Upload them on the Scanner Audit page first.`); setBusy(false); return; }
      const freshDays = new Set(j.events.map((e) => e.day));
      const kept = (parsed && parsed.events ? parsed.events : []).filter((e) => !freshDays.has(e.day));
      const events = [...kept, ...j.events];
      const drivers = [...new Set(events.map((e) => e.driver))].sort();
      const days = new Set(events.map((e) => e.day)).size;
      const next = { contract: j.contract || (parsed && parsed.contract) || null, events, drivers, days };
      setParsed(next);
      setDriver((d) => (d && drivers.includes(d) ? d : (drivers.includes("CHENZ19") ? "CHENZ19" : drivers[0] || "")));
      if (onParsed) onParsed(next);
    } catch (e) { setErr("Couldn't load stored audits — " + e.message); }
    setBusy(false);
  }, [onParsed, parsed]);

  // Jump-from-Complete: pre-select a driver when the page pushes one in.
  useEffect(() => {
    if (focusDriver) { setDriver(focusDriver); }
  }, [focusDriver]);

  const regEntry = registry[driver] || null;
  const dayList = useMemo(() => (parsed ? [...new Set(parsed.events.map((e) => e.day))].sort() : []), [parsed]);
  const activeDay = dayView !== "all" && dayList.includes(dayView) ? dayView : "all";
  const planEvents = useMemo(
    () => (!parsed ? [] : activeDay === "all" ? parsed.events : parsed.events.filter((e) => e.day === activeDay)),
    [parsed, activeDay]
  );
  /* Two streets scanned seconds apart — same spot, so it's a corner address, a
     mis-delivery, or AP address junk. Session-only, like the section moves. */
  const closePairs = useMemo(
    () => (parsed && driver ? detectCloseScans(parsed.events, driver) : []),
    [parsed, driver]
  );
  const closeCalls = useMemo(() => closeByDriver[driver] || {}, [closeByDriver, driver]);
  // Of a pair, keep the street that's actually on this driver's route list; the other is
  // the alias (same place) or the wrong drop (mis-delivery). If neither is on the list
  // it's AP junk either way, so keep the first and merge the second into it.
  const sideOf = useCallback((p) => {
    const on = (k) => (regEntry?.routes || []).some((rt) => rt.match === canonStreetName(k));
    if (on(p.a) && !on(p.b)) return { keep: p.a, other: p.b };
    if (on(p.b) && !on(p.a)) return { keep: p.b, other: p.a };
    return { keep: p.a, other: p.b };
  }, [regEntry]);
  const closeAliases = useMemo(() => {
    const m = {};
    closePairs.forEach((p) => { if (closeCalls[p.id] === "same") { const { keep, other } = sideOf(p); m[other] = keep; } });
    return m;
  }, [closePairs, closeCalls, sideOf]);
  const closeDrops = useMemo(() => {
    const s = new Set();
    closePairs.forEach((p) => { if (closeCalls[p.id] === "mis") s.add(sideOf(p).other); });
    return s;
  }, [closePairs, closeCalls, sideOf]);
  const openPairs = useMemo(() => closePairs.filter((p) => !closeCalls[p.id]), [closePairs, closeCalls]);
  const setCall = (id, v) => setCloseByDriver((all) => {
    const o = { ...(all[driver] || {}) };
    if (v == null) delete o[id]; else o[id] = v;
    return { ...all, [driver]: o };
  });

  /* Addresses whose house number can't be real — an apartment number run into the street
     number. Detected on the raw week, then corrected or struck out by hand. */
  const badAddrs = useMemo(
    () => (parsed && driver ? detectBadAddresses(parsed.events, driver) : []),
    [parsed, driver]
  );
  const addrCalls = useMemo(() => addrByDriver[driver] || {}, [addrByDriver, driver]);
  const addrEdits = useMemo(() => {
    const m = {};
    Object.entries(addrCalls).forEach(([id, v]) => { if (v && v !== "remove" && v.to) m[id] = v.to; });
    return m;
  }, [addrCalls]);
  const dropAddrs = useMemo(
    () => new Set(Object.entries(addrCalls).filter(([, v]) => v === "remove").map(([id]) => id)),
    [addrCalls]
  );
  const openAddrs = useMemo(() => badAddrs.filter((a) => !addrCalls[a.id]), [badAddrs, addrCalls]);
  const setAddr = (id, v) => setAddrByDriver((all) => {
    const o = { ...(all[driver] || {}) };
    if (v == null) delete o[id]; else o[id] = v;
    return { ...all, [driver]: o };
  });

  // perDay:false → actual parcel counts (collective total when "all", that day's count when one day is picked)
  const plan = useMemo(
    () => (parsed && driver ? buildPlan(planEvents, driver, regEntry, { perDay: false, aliases: closeAliases, dropStreets: closeDrops, addrEdits, dropAddrs }) : null),
    [planEvents, parsed, driver, regEntry, closeAliases, closeDrops, addrEdits, dropAddrs]
  );

  const base = useMemo(() => (plan ? nominateSections(plan.streets) : []), [plan]);
  // Keyed by visit id, not street name — a street worked in two passes is two rows,
  // and moving the morning pass mustn't drag the afternoon one with it.
  const autoOf = useMemo(() => { const m = {}; base.forEach((s) => (m[s.id] = s.section)); return m; }, [base]);
  const overrides = useMemo(() => ovByDriver[driver] || {}, [ovByDriver, driver]); // the current driver's slice
  const nominated = useMemo(
    () => withDeliveryOrder(base.map((s) => {
      const ov = overrides[s.id];
      return { ...s, autoSection: s.section, section: ov ?? s.section, moved: ov != null && ov !== s.section };
    })), // re-number: a street pulled out shouldn't leave a hole in the run order
    [base, overrides]
  );
  const sections = useMemo(() => groupSections(nominated), [nominated]);

  /* Hand a street — or a whole section — to another driver.

     Distinct from the A·B·C·D pills, which shuffle a street between THIS driver's bags.
     This changes who delivers it: it writes Driver_Routes and logs for AP, so it's the
     same weight as a Transfer-tab move, just reachable from the plan you're reading.

     The streets go as one list, so a section lands on AP's desk as one entry naming all
     of them rather than seven separate ones. They also drop out of this driver's plan
     immediately — the sections re-balance and the run order renumbers, which is the whole
     point of moving them. */
  const [movingTo, setMovingTo] = useState(null);   // {kind:'street'|'section', id, section, label}
  const [moveDriver, setMoveDriver] = useState("");
  const otherDrivers = useMemo(
    () => Object.keys(registry || {})
      .filter((d) => d !== driver)
      .sort((a, b) => {
        const sameArea = (x) => (registry[x]?.area === regEntry?.area ? 0 : 1); // this driver's area first
        return sameArea(a) - sameArea(b) || a.localeCompare(b);
      }),
    [registry, driver, regEntry]
  );
  const startMove = (m) => { setMovingTo(m); setMoveDriver(otherDrivers[0] || ""); };
  const doMove = () => {
    if (!movingTo || !moveDriver || !onRepair) return;
    const rows = movingTo.kind === "section"
      ? sections[movingTo.section].filter((s) => s.section !== 0)
      : nominated.filter((s) => s.id === movingTo.id);
    // A street worked in two passes is two rows but ONE street on the route list — send
    // the label once, or the target driver's list grows a duplicate.
    const labels = [...new Set(rows.map((s) => s.street))];
    if (!labels.length) return;
    // The street leaves this driver entirely, so EVERY pass of it goes — including passes
    // sitting in other sections. Count those in, or the dialog under-reports what moves.
    const affected = nominated.filter((s) => s.section !== 0 && labels.includes(s.street));
    const parcels = affected.reduce((a, s) => a + s.parcels, 0);
    const elsewhere = affected.filter((s) => !rows.some((r) => r.id === s.id));
    const what = movingTo.kind === "section"
      ? `Section ${sectionCode(movingTo.section)} — ${labels.length} street${labels.length > 1 ? "s" : ""}, ${parcels} parcels`
      : `“${labels[0]}” (${parcels} parcels)`;
    const alsoNote = elsewhere.length
      ? `\n\nNote: ${elsewhere.length} further pass${elsewhere.length > 1 ? "es" : ""} of ${elsewhere.length > 1 ? "these streets" : "this street"} sit${elsewhere.length > 1 ? "" : "s"} in ${[...new Set(elsewhere.map((s) => "Section " + sectionCode(s.section)))].join(", ")} and will move too — the street leaves ${driver} altogether.`
      : "";
    const ok = typeof window === "undefined" || !window.confirm || window.confirm(
      `Move ${what} from ${driver} to ${moveDriver}?\n\n${labels.join("\n")}\n\n` +
      `${moveDriver} takes ${labels.length > 1 ? "these streets" : "this street"} on their route list; ${driver} loses ${labels.length > 1 ? "them" : "it"}. ` +
      `Saves live to Driver_Routes and logs one change for AP.${alsoNote}`
    );
    if (!ok) return;
    labels.forEach((l) => moveNamedStreet(l, 0)); // out of this driver's plan now
    onRepair({ type: "move", driver: moveDriver, streets: labels, fromDriver: driver });
    setMovingTo(null);
    setSnapNote(`Moved ${what} → ${moveDriver}. Logged for AP.`);
  };

  const owners = useMemo(() => buildOwners(registry), [registry]); // canon street → drivers who own it (for wrong-driver call-outs)
  // Contract-wide, not this driver's — a street on nobody's list is everyone's problem.
  const missing = useMemo(() => (parsed ? missingStreets(parsed.events, owners) : []), [parsed, owners]);
  const [showMissing, setShowMissing] = useState(false);
  const missingParcels = missing.reduce((a, s) => a + s.perDay, 0);
  const flags = useMemo(() => detectFlags(nominated, regEntry, sections, driver, owners), [nominated, regEntry, sections, driver, owners]);
  const acked = useMemo(() => ackByDriver[driver] || {}, [ackByDriver, driver]); // streets this driver has marked reviewed
  const visibleFlags = useMemo(() => flags.filter((f) => !(f.idx != null && acked[f.ref])), [flags, acked]);
  const ackedFlags = useMemo(() => flags.filter((f) => f.idx != null && acked[f.ref]), [flags, acked]);
  const markReviewed = (street) => setAckByDriver((all) => ({ ...all, [driver]: { ...(all[driver] || {}), [street]: true } }));
  const unReview = (street) => setAckByDriver((all) => { const a = { ...(all[driver] || {}) }; delete a[street]; return { ...all, [driver]: a }; });
  const totalParcels = nominated.reduce((a, s) => a + s.parcels, 0);
  const excluded = useMemo(() => nominated.filter((s) => s.section === 0), [nominated]); // pulled out of the sort plan
  const excludedParcels = excluded.reduce((a, s) => a + s.parcels, 0);
  const inPlanParcels = totalParcels - excludedParcels;

  // Inline repair: turn an off-list flag into a one-click route-list fix (parent writes Driver_Routes live).
  const repairFor = (street) => {
    if (!onRepair || !regEntry) return null;
    const { flag } = reviewStreet(street, driver, registry, owners);
    if (!flag || flag === "UNNAMED") return null;            // clean or unclear → nothing safe to do
    const set = owners[canonStreetName(street)];
    const other = set ? [...set].filter((d) => d !== driver) : [];
    if (flag === "OTHER" && other.length === 1) {
      return { label: `Move ${other[0]} → ${driver}`, type: "move", fromDriver: other[0],
        confirm: `Move “${street}” from ${other[0]} to ${driver}?\n\nRemoves it from ${other[0]}'s route and adds it to ${driver}'s. Saves live to Driver_Routes and logs the change for AP.` };
    }
    const why = flag === "TYPO" ? " — AP's spelling of this driver's own street"
      : flag === "OFFLIST" ? " — currently on no route"
      : other.length ? ` — also on ${other.join(", ")}, becomes a shared street` : "";
    return { label: `Add to ${driver}`, type: "add",
      confirm: `Add “${street}” to ${driver}'s route list?${why}\n\nSaves live to Driver_Routes and logs the change for AP.` };
  };
  const onFixClick = (act, street) => {
    if (!act) return;
    if (typeof window !== "undefined" && window.confirm && !window.confirm(act.confirm)) return;
    onRepair({ type: act.type, driver, street, fromDriver: act.fromDriver || null });
  };

  // Wrong-driver street: this driver only *delivered* a street that's another driver's usual
  // route (relief / wrong-driver scan). Confirm the usual owner and pull it off this driver —
  // excluded from his plan so it disappears, and logged for AP as {driver} → {owner}.
  const removeFor = (street) => {
    if (!onRepair || !regEntry) return null;
    const canon = canonStreetName(street);
    if ((regEntry.routes || []).some((rt) => rt.match === canon)) return null; // already his own street
    const set = owners[canon];
    const other = set ? [...set].filter((d) => d !== driver) : [];
    if (other.length !== 1) return null; // one clear usual driver only
    const usual = other[0];
    return {
      usualDriver: usual,
      label: `${usual}'s — remove`,
      confirm: `Confirm “${street}” is ${usual}'s usual street and remove it from ${driver}?\n\n${driver} only delivered it (relief / wrong-driver scan). It drops out of ${driver}'s sort plan and is logged for AP as ${driver} → ${usual}. ${usual} keeps the street.`,
    };
  };
  const onRemoveClick = (act, street) => {
    if (!act) return;
    if (typeof window !== "undefined" && window.confirm && !window.confirm(act.confirm)) return;
    moveNamedStreet(street, 0); // pull it out of this driver's sort plan → disappears + flag clears
    onRepair({ type: "move", driver: act.usualDriver, street, fromDriver: driver }); // log {driver} → {owner}, strip from his list if present
  };

  // "Add a street": search every driver's route list so you can pull a recognised street
  // onto this driver. Each entry knows which driver(s) currently hold it.
  const streetIndex = useMemo(() => {
    const m = new Map(); // canon → { canon, label, owners:{driver→that driver's label} }
    Object.keys(registry || {}).forEach((dv) => (registry[dv].routes || []).forEach((rt) => {
      if (!rt.match) return;
      let e = m.get(rt.match);
      if (!e) { e = { canon: rt.match, label: rt.label || titleCase(rt.match), owners: {} }; m.set(rt.match, e); }
      e.owners[dv] = rt.label || e.label;
    }));
    return [...m.values()];
  }, [registry]);
  const addMatches = useMemo(() => {
    const q = addQ.trim().toUpperCase();
    if (q.length < 2) return [];
    return streetIndex
      .filter((e) => e.canon.includes(q) || String(e.label).toUpperCase().includes(q))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)))
      .slice(0, 12);
  }, [addQ, streetIndex]);
  const addExact = useMemo(() => {
    const q = addQ.trim().toUpperCase();
    if (!q) return true;
    const canon = canonStreetName(addQ);
    return addMatches.some((e) => e.canon === canon || String(e.label).toUpperCase() === q);
  }, [addQ, addMatches]);
  const addStreet = (type, entry, fromDriver) => {
    if (!onRepair || !driver) return;
    const label = fromDriver ? (entry.owners[fromDriver] || entry.label) : entry.label;
    const others = Object.keys(entry.owners).filter((d) => d !== driver);
    const confirm = type === "move"
      ? `Move “${label}” from ${fromDriver} to ${driver}?\n\nRemoves it from ${fromDriver}'s route and adds it to ${driver}'s. Saves live to Driver_Routes and logs the change for AP.`
      : `Add “${label}” to ${driver}'s route list?${others.length ? ` — also on ${others.join(", ")}, becomes a shared street` : ""}\n\nSaves live to Driver_Routes and logs the change for AP.`;
    if (typeof window !== "undefined" && window.confirm && !window.confirm(confirm)) return;
    onRepair({ type, driver, street: label, fromDriver: fromDriver || null });
    setAddQ("");
  };
  const addRaw = () => {
    if (!onRepair || !driver) return;
    const label = titleCase(addQ.trim());
    if (!label) return;
    const confirm = `Add “${label}” to ${driver}'s route list?\n\nIt isn't on any current route — added as a new street. Saves live to Driver_Routes and logs the change for AP.`;
    if (typeof window !== "undefined" && window.confirm && !window.confirm(confirm)) return;
    onRepair({ type: "add", driver, street: label, fromDriver: null });
    setAddQ("");
  };

  // Unrecognised streets (OFFLIST/UNNAMED): either junk (✕ Bad address) or a misspelling the
  // auto-fuzzy missed. "Correct spelling" suggests the closest real names across every driver's
  // routes (Levenshtein), or type to search; picking one resolves it.
  const correctSuggest = useMemo(() => {
    if (!correcting) return [];
    const canon = canonStreetName(correcting);
    const q = correctQ.trim().toUpperCase();
    let list = streetIndex;
    if (q.length >= 2) list = list.filter((e) => e.canon.includes(q) || String(e.label).toUpperCase().includes(q));
    return [...list].map((e) => ({ ...e, d: lev(canon, e.canon) }))
      .sort((a, b) => a.d - b.d || String(a.label).localeCompare(String(b.label)))
      .slice(0, 8);
  }, [correcting, correctQ, streetIndex]);
  const startCorrect = (street) => { setCorrecting((c) => (c === street ? null : street)); setCorrectQ(""); };

  // Range-split shared streets: which driver's number range covers a given house number.
  const rangeOwners = useMemo(() => {
    const m = {}; // canon street → [{ driver, ranges }]
    Object.keys(registry || {}).forEach((dv) => (registry[dv].routes || []).forEach((rt) => {
      if (rt.match && rt.ranges) (m[rt.match] = m[rt.match] || []).push({ driver: dv, ranges: rt.ranges });
    }));
    return m;
  }, [registry]);
  const ownerForNumber = (canon, n) => {
    const list = rangeOwners[canon] || [];
    // Prefer an odd/even-aware match (AP usually splits a street by side)…
    for (const o of list) if (o.ranges.some((rg) => n >= rg.lo && n <= rg.hi && (rg.parity == null || n % 2 === rg.parity))) return o.driver;
    // …but if nothing matches on parity, fall back to a plain lo–hi match so a full-range
    // label (e.g. "1-50" meaning all numbers) still resolves instead of stranding the number.
    for (const o of list) if (o.ranges.some((rg) => n >= rg.lo && n <= rg.hi)) return o.driver;
    return null;
  };
  const startNums = (street) => setReviewingNums((s) => (s === street ? null : street));
  const assignNums = (key, toDriver, nums) => {
    if (!onRepair || !toDriver) return;
    const name = titleCase(key);
    const streets = nums.map((n) => `${name} ${n}`);
    if (typeof window !== "undefined" && window.confirm && !window.confirm(`Assign ${streets.length} ${name} address${streets.length > 1 ? "es" : ""} (${nums.join(", ")}) from ${driver} to ${toDriver}?\n\nLogs the addresses as a transfer for AP. Route ranges are not changed.`)) return;
    onRepair({ type: "assign", driver: toDriver, fromDriver: driver, streets });
  };
  // Add a (usually range-split) street to THIS driver's own route, with his number range.
  const startAddRange = (street, canon) => { setAddingRange((s) => (s === street ? null : street)); setRangeInput(titleCase(canon) + " "); };
  const submitAddRange = () => {
    const label = rangeInput.trim();
    if (!onRepair || !label || !driver) return;
    if (typeof window !== "undefined" && window.confirm && !window.confirm(`Add “${label}” to ${driver}'s route list?\n\nInclude a range like "302-512, 401-565" so it only covers his side. Saves to Driver_Routes and logs for AP.`)) return;
    onRepair({ type: "add", driver, street: label, fromDriver: null });
    setAddingRange(null); setRangeInput("");
  };
  const onBadAddress = (street) => {
    if (typeof window !== "undefined" && window.confirm && !window.confirm(`Mark “${street}” as a bad address and remove it from ${driver}'s plan?\n\nIt moves to the Excluded tray (your record of removed addresses). No route or AP change — reversible from the tray.`)) return;
    moveNamedStreet(street, 0);
  };
  const applyCorrection = (originalStreet, entry) => {
    if (!onRepair) return;
    const onOwn = Object.keys(entry.owners).includes(driver);
    const others = Object.keys(entry.owners).filter((d) => d !== driver);
    if (onOwn) {
      if (typeof window !== "undefined" && window.confirm && !window.confirm(`“${originalStreet}” is AP's spelling of your own “${entry.label}”. Add it to ${driver} so it's recognised?\n\nSaves to Driver_Routes and logs for AP.`)) return;
      onRepair({ type: "add", driver, street: originalStreet, fromDriver: null });
    } else if (others.length) {
      const owner = others[0];
      if (typeof window !== "undefined" && window.confirm && !window.confirm(`“${originalStreet}” is ${owner}'s “${entry.label}” (AP misspelling). Remove it from ${driver} and log as ${driver} → ${owner}?\n\n${owner} keeps the street. Logged for AP.`)) return;
      moveNamedStreet(originalStreet, 0);
      onRepair({ type: "move", driver: owner, street: entry.label, fromDriver: driver });
    } else { return; }
    setCorrecting(null); setCorrectQ("");
  };

  const moveStreet = (id, sec) => setOvByDriver((all) => {
    const o = { ...(all[driver] || {}) };
    if (sec === autoOf[id]) delete o[id]; else o[id] = sec;
    return { ...all, [driver]: o };
  });
  /* Section moves are keyed by VISIT id, because a street worked in two passes is two rows
     and each has to move on its own. The review flags, though, act on a street by NAME and
     always mean the whole street — every pass of it. Give them their own door rather than
     letting a name reach moveStreet, where it silently writes an override no visit can
     ever match: the street stays put, the flag never clears, and the button looks dead. */
  const moveNamedStreet = (street, sec) => setOvByDriver((all) => {
    const o = { ...(all[driver] || {}) };
    nominated.filter((s) => s.street === street).forEach((s) => {
      if (sec === autoOf[s.id]) delete o[s.id]; else o[s.id] = sec;
    });
    return { ...all, [driver]: o };
  });
  const movedCount = Object.keys(overrides).length;
  const sectionSummary = (n) => {
    const g = sections[n]; if (!g.length) return null;
    return { count: g.length, parcels: g.reduce((a, s) => a + s.parcels, 0), lo: Math.min(...g.map((x) => toMinutes(x.first))), hi: Math.max(...g.map((x) => toMinutes(x.last))) };
  };
  const axisTicks = [8, 9, 10, 11, 12, 13].map((h) => h * 60);
  const bays = regEntry?.bays, chute = regEntry?.chute, contract = regEntry?.contract || parsed?.contract;

  // The driver's own section names (from Section Scan), tagged onto streets by time.
  const scannerSections = useMemo(() => loadScannerSections(), [parsed, driver]);
  const driverSecs = scannerSections[driver] || [];

  // Export-scope dropdown options (drivers in the loaded data + the contract areas present).
  const exportDrivers = useMemo(() => (parsed ? [...new Set(parsed.events.map((e) => e.driver))].sort() : []), [parsed]);
  const exportAreas = useMemo(() => [...new Set(exportDrivers.map((d) => registry[d] && registry[d].area).filter(Boolean))].sort(), [exportDrivers, registry]);

  // Export every driver's sort plan to a styled .xlsx (lazy-load the styling lib).
  const exportXlsx = async (review = false) => {
    if (!parsed) return;
    setBusy(true);
    try {
      const _xs = await import("xlsx-js-style");
      const XS = _xs && _xs.default && _xs.default.utils ? _xs.default : _xs;
      const scn = loadScannerSections();
      const SEC_FILL = { 1: "1B9C84", 2: "D69A2D", 3: "C0563E", 4: "5667C6" }; // teal / gold / terracotta / indigo (engine colours)
      const REVIEW_FILL = { UNNAMED: "FFE08A", OTHER: "F4B6B6", TYPO: "BFE0F4", OFFLIST: "FBD38D" }; // yellow / red / blue / orange
      const headers = ["Delivery order", "Street Name and Number range", "Pass", "Pause / resume point", "Driver number", "Driver Login", "Contract Area", "Chute number", "Outlet number", "Bay number", "Section Name", "Sort Section"];
      if (review) headers.push("Review flag");
      const ORDCOL = 0, STCOL = 1, PAUSECOL = 3;
      const SORTCOL = 11;                // Sort Section column — always teal/gold/terracotta/indigo
      const LAST = headers.length - 1;   // last column (= Sort Section, or Review flag in review mode)
      const aoa = [headers];
      const blocks = [];
      const flagRows = [];               // review: one flag per data row, parallel to aoa[1..]
      const perDriver = [];              // review: per-driver flag tally for the Summary tab
      let allDrivers = [...new Set(parsed.events.map((e) => e.driver))].sort();
      if (exportScope.startsWith("area:")) { const a = exportScope.slice(5); allDrivers = allDrivers.filter((d) => (registry[d] && registry[d].area) === a); }
      else if (exportScope.startsWith("driver:")) { const d0 = exportScope.slice(7); allDrivers = allDrivers.filter((d) => d === d0); }
      for (const dv of allDrivers) {
        const reg = registry[dv] || {};
        const pl = buildPlan(parsed.events, dv, reg, { perDay: false });
        if (!pl.streets.length) continue;
        const ov = ovByDriver[dv] || {}; // honour this driver's hand-moves + exclusions in the download
        const nom = withDeliveryOrder( // re-number after exclusions so AP gets 1..N with no gaps
          nominateSections(pl.streets)
            .map((s) => ({ ...s, section: ov[s.id] ?? s.section }))
            .filter((s) => s.section !== 0) // section 0 = pulled out of the plan
        );
        if (!nom.length) continue;
        const secs = scn[dv] || [];
        const dm = String(reg.adam || reg.dNumber || dv).match(/D\d+/i); // round number e.g. D1, D13
        const driverNum = dm ? dm[0].toUpperCase() : dv;
        const bm = String(reg.adam || "").match(/BAY[_ -]?(\d+)/i);       // bay can be read off the ADAM code
        const bayVal = reg.bays || (bm ? bm[1] : "");
        const start = aoa.length;
        const tally = { clean: 0, UNNAMED: 0, OTHER: 0, TYPO: 0, OFFLIST: 0 };
        nom.forEach((s) => {
          // Where a street is worked in two goes, say so and give the number it breaks
          // off at / picks up from — that's the boundary AP needs to sort against.
          const pause = s.stopNum != null ? `Pauses at ${s.stopNum}`
            : s.startNum != null ? `Resumes at ${s.startNum}`
            : s.boundaryVaries ? "Boundary varies" : "";
          const row = [
            s.order, s.street, s.passes > 1 ? `${s.pass} of ${s.passes}` : "", pause,
            driverNum, dv, reg.area || "", reg.chute || "", "", bayVal,
            sectionForTime(secs, toMinutes(s.median)) || "", sectionCode(s.section),
          ];
          if (review) { const { flag, note } = reviewStreet(s.street, dv, registry, owners); row.push(note); flagRows.push(flag); tally[flag || "clean"]++; }
          aoa.push(row);
        });
        if (review) perDriver.push({ driverNum, dv, area: reg.area || "", total: nom.length, ...tally });
        blocks.push({ start, end: aoa.length - 1, secByRow: nom.map((s) => s.section) });
      }
      const ws = XS.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [{ wch: 13 }, { wch: 36 }, { wch: 8 }, { wch: 19 }, { wch: 13 }, { wch: 14 }, { wch: 13 }, { wch: 13 }, { wch: 14 }, { wch: 12 }, { wch: 26 }, { wch: 13 }].concat(review ? [{ wch: 46 }] : []);
      const thin = { style: "thin", color: { rgb: "D6DBE0" } };
      const thick = { style: "medium", color: { rgb: "16202B" } };
      for (let c = 0; c <= LAST; c++) {
        const cell = ws[XS.utils.encode_cell({ r: 0, c })];
        if (cell) cell.s = { font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 }, fill: { fgColor: { rgb: "16202B" } }, alignment: { horizontal: "center", vertical: "center", wrapText: true }, border: { top: thin, bottom: thin, left: thin, right: thin } };
      }
      blocks.forEach((b) => {
        for (let r = b.start; r <= b.end; r++) {
          const sec = b.secByRow[r - b.start];
          const fl = review ? flagRows[r - 1] : null;
          for (let c = 0; c <= LAST; c++) {
            const cell = ws[XS.utils.encode_cell({ r, c })];
            if (!cell) continue;
            const border = { top: r === b.start ? thick : thin, bottom: r === b.end ? thick : thin, left: c === 0 ? thick : thin, right: c === LAST ? thick : thin };
            if (c === SORTCOL) cell.s = { border, fill: { fgColor: { rgb: SEC_FILL[sec] || "888888" } }, font: { bold: true, color: { rgb: "FFFFFF" } }, alignment: { horizontal: "center", vertical: "center" } };
            else if (c === ORDCOL) cell.s = { border, fill: { fgColor: { rgb: "16202B" } }, font: { bold: true, color: { rgb: "FFFFFF" } }, alignment: { horizontal: "center", vertical: "center" } };
            else if (c === PAUSECOL && cell.v) cell.s = { border, fill: { fgColor: { rgb: "FBF1DC" } }, font: { bold: true, color: { rgb: "7A4A12" } }, alignment: { horizontal: "center", vertical: "center" } };
            else if (review && fl) cell.s = { border, fill: { fgColor: { rgb: REVIEW_FILL[fl] } }, alignment: { vertical: "center", wrapText: c === STCOL || c === LAST } };
            else cell.s = { border, alignment: { vertical: "center", wrapText: c === STCOL } };
          }
        }
      });
      const wb = XS.utils.book_new();
      if (review && perDriver.length) {
        // Ranked summary — most mis-sorts (red) first, so AP gets a worst-offenders list up top.
        const sh = [["Driver number", "Driver Login", "Contract Area", "Streets", "On another driver", "Unclear address", "AP typo (own)", "Off-list", "Flags total"]];
        const ranked = perDriver.map((d) => ({ ...d, flagsTotal: d.OTHER + d.UNNAMED + d.TYPO + d.OFFLIST }))
          .sort((a, b) => b.OTHER - a.OTHER || b.flagsTotal - a.flagsTotal || b.total - a.total);
        ranked.forEach((d) => sh.push([d.driverNum, d.dv, d.area, d.total, d.OTHER, d.UNNAMED, d.TYPO, d.OFFLIST, d.flagsTotal]));
        const tot = ranked.reduce((a, d) => ({ total: a.total + d.total, OTHER: a.OTHER + d.OTHER, UNNAMED: a.UNNAMED + d.UNNAMED, TYPO: a.TYPO + d.TYPO, OFFLIST: a.OFFLIST + d.OFFLIST, flagsTotal: a.flagsTotal + d.flagsTotal }), { total: 0, OTHER: 0, UNNAMED: 0, TYPO: 0, OFFLIST: 0, flagsTotal: 0 });
        sh.push(["TOTAL", "", "", tot.total, tot.OTHER, tot.UNNAMED, tot.TYPO, tot.OFFLIST, tot.flagsTotal]);
        const wsSum = XS.utils.aoa_to_sheet(sh);
        wsSum["!cols"] = [{ wch: 13 }, { wch: 14 }, { wch: 13 }, { wch: 9 }, { wch: 17 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 12 }];
        const SUMLAST = 8;
        const COLFILL = { 4: "F4B6B6", 5: "FFE08A", 6: "BFE0F4", 7: "FBD38D" }; // tint each flag column its colour
        for (let c = 0; c <= SUMLAST; c++) { const cell = wsSum[XS.utils.encode_cell({ r: 0, c })]; if (cell) cell.s = { font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 }, fill: { fgColor: { rgb: "16202B" } }, alignment: { horizontal: "center", vertical: "center", wrapText: true }, border: { top: thin, bottom: thin, left: thin, right: thin } }; }
        for (let r = 1; r < sh.length; r++) {
          const isTot = r === sh.length - 1;
          for (let c = 0; c <= SUMLAST; c++) {
            const cell = wsSum[XS.utils.encode_cell({ r, c })]; if (!cell) continue;
            const fill = COLFILL[c] || (isTot ? "E9EDF1" : null);
            cell.s = { border: { top: isTot ? thick : thin, bottom: thin, left: thin, right: thin }, alignment: { horizontal: c <= 2 ? "left" : "center", vertical: "center" }, font: { bold: isTot || c >= 4 }, ...(fill ? { fill: { fgColor: { rgb: fill } } } : {}) };
          }
        }
        XS.utils.book_append_sheet(wb, wsSum, "Summary");
      }
      XS.utils.book_append_sheet(wb, ws, review ? "Route Review" : "Sort Plan");
      const out = XS.write(wb, { type: "array", bookType: "xlsx" });
      const blob = new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "route-sections-" + (review ? "review" : "sort-plan") + "-" + (exportScope === "all" ? "all" : exportScope.split(":")[1]) + ".xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setErr("Couldn't build the Excel file."); }
    setBusy(false);
  };

  return (
    <div className={"rse" + (fileDrag ? " filedrag" : "")}
      onDragOver={(e) => { if ([...(e.dataTransfer.types || [])].includes("Files")) { e.preventDefault(); if (!fileDrag) setFileDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setFileDrag(false); }}
      onDrop={(e) => { if (e.dataTransfer.files && e.dataTransfer.files.length) { e.preventDefault(); setFileDrag(false); onFiles(e.dataTransfer.files); } }}>
      <style>{CSS}</style>

      <div className="rse-top">
        <div>
          <div className="rse-eyebrow">Driver Routes · Sort Plan</div>
          <div className="rse-title">Route Sections</div>
        </div>
        <div className="rse-load">
          <label className="rse-drop">
            <input type="file" accept=".xlsx,.xls" multiple style={{ display: "none" }}
              onChange={(e) => onFiles(e.target.files)} />
            <span>{busy ? "Reading…" : parsed ? "Add / replace day(s)" : "Drop Scanner Audit .xlsx"}</span>
          </label>
          <span className="rse-stored" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            title="Build the plan from audits already uploaded on the Scanner Audit page — no file drop needed">
            <input type="date" className="rse-sel" value={storedFrom} max={storedTo} onChange={(e) => setStoredFrom(e.target.value)} />
            <span style={{ color: "var(--hp-dim, #888)", fontSize: ".8rem" }}>→</span>
            <input type="date" className="rse-sel" value={storedTo} min={storedFrom} onChange={(e) => setStoredTo(e.target.value)} />
            <button className="rse-save" onClick={() => loadStored(storedFrom, storedTo)} disabled={busy || !storedFrom || !storedTo}>
              {busy ? "Loading…" : "Load stored"}
            </button>
          </span>
          {parsed && (
            <select className="rse-sel" value={driver} onChange={(e) => setDriver(e.target.value)}>
              {parsed.drivers.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          {parsed && (
            <span className="rse-retain" title={login ? "Saved to your account — survives browser clears and follows you across devices. Drop new files to replace it." : "Saved in this browser. Drop new files to replace it."}>
              <span className="rse-retain-dot" style={cloud === "syncing" ? { background: "#C8A24B" } : undefined} />
              {cloud === "syncing" ? "Syncing…" : (login ? "Saved" : "Retained")}
              <button className="rse-clear" onClick={clearSaved} disabled={!!snapBusy}
                title={login ? "Clear the screen — saved to Saves first, so you can reopen it" : "Clear the screen — you're not signed in, so this can't be undone"}>Clear</button>
            </span>
          )}
          {parsed && login && (
            <button className="rse-save" onClick={onSaveClick} disabled={!!snapBusy}
              title="Save this project — the week, your section moves, reviewed flags and address calls — dated, so you can come back to it">
              {snapBusy === "saving" ? "Saving…" : "✔ Save project"}
            </button>
          )}
          {login && snaps.length > 0 && (
            <button className={"rse-snaps-b" + (snapsOpen ? " on" : "")} onClick={() => setSnapsOpen((v) => !v)}>
              Saves · {snaps.length}
            </button>
          )}
          {parsed && (
            <span className="rse-exp">
              <select className="rse-expsel" value={exportScope} onChange={(e) => setExportScope(e.target.value)}
                title="What to include in the Excel download">
                <option value="all">All drivers</option>
                {exportAreas.length > 0 && (
                  <optgroup label="Contract area">
                    {exportAreas.map((a) => <option key={a} value={"area:" + a}>{a}</option>)}
                  </optgroup>
                )}
                <optgroup label="One driver">
                  {exportDrivers.map((d) => <option key={d} value={"driver:" + d}>{d}</option>)}
                </optgroup>
              </select>
              <button className="rse-export" onClick={() => exportXlsx(false)} disabled={busy}
                title="Download the sort plan as a styled Excel file">⤓ Excel</button>
              <button className="rse-export rse-review" onClick={() => exportXlsx(true)} disabled={busy}
                title="Download a flagged review — colour-coded: red = on another driver's route, yellow = unclear address, blue = AP typo of own street, orange = off-list">⚑ Review</button>
            </span>
          )}
        </div>
      </div>

      {err && <div className="rse-err">{err}</div>}
      {snapNote && <div className="rse-snapnote">{snapNote}<button onClick={() => setSnapNote("")}>✕</button></div>}

      {/* Dated project saves. Shown even with nothing loaded — that's exactly when you need
          to get a week back. */}
      {login && snapsOpen && snaps.length > 0 && (
        <div className="rse-snaps">
          <div className="rse-snaps-h">
            <b>Saved projects</b>
            <span>The week plus your section moves, reviewed flags and address calls. The last {MAX_SNAPS} are kept.</span>
          </div>
          {snaps.map((s) => (
            <div key={s.key} className="rse-snap">
              <div className="rse-snap-l">
                <div className="rse-snap-t">
                  {s.label}
                  {s.note && <span className="rse-snap-note">{s.note}</span>}
                </div>
                <div className="rse-snap-s mono">
                  {fmtSavedAt(s.savedAt)} · {s.days} day{s.days > 1 ? "s" : ""} · {s.scans.toLocaleString()} scans
                  {s.moves ? ` · ${s.moves} move${s.moves > 1 ? "s" : ""}` : ""}
                  {s.acks ? ` · ${s.acks} reviewed` : ""}
                  {s.driver ? ` · ${s.driver}` : ""}
                </div>
              </div>
              <button className="rse-snap-open" disabled={!!snapBusy} onClick={() => restoreProject(s)}>
                {snapBusy === "restoring" ? "Opening…" : "Open"}
              </button>
            </div>
          ))}
        </div>
      )}

      {!parsed && login && snaps.length > 0 && !snapsOpen && (
        <button className="rse-snaps-cta" onClick={() => setSnapsOpen(true)}>
          ↩ You have {snaps.length} saved project{snaps.length > 1 ? "s" : ""} — open one instead of re-dropping files
        </button>
      )}

      {!parsed && (
        <div className="rse-empty-state">
          <div className="rse-es-ic">⤓</div>
          <div className="rse-es-t">Drop your Scanner Audit export to build the sort plan</div>
          <div className="rse-es-s">One file per day — drop several together or add days over time; then toggle each day or the weekly total. Parsed in your browser; nothing is uploaded.</div>
        </div>
      )}

      {parsed && plan && (
        <>
          {dayList.length > 1 && (
            <div className="rse-days">
              <span className="rse-days-l">View</span>
              <button className={"rse-day" + (activeDay === "all" ? " on" : "")} onClick={() => setDayView("all")}>Total · {dayList.length} days</button>
              {dayList.map((dk, i) => (
                <button key={dk} className={"rse-day" + (activeDay === dk ? " on" : "")} onClick={() => setDayView(dk)}>{dayLabelOf(dk, i)}</button>
              ))}
            </div>
          )}
          <div className="rse-meta">
            <Cell k="Driver Login" v={driver} mono />
            <Cell k="Contract" v={contract || "—"} mono dim={!contract} />
            <Cell k="Bays" v={bays || "—"} mono dim={!bays} note={!bays ? "from registry" : ""} />
            <Cell k="Chute" v={chute || "—"} mono dim={!chute} note={!chute ? "from registry" : ""} />
            <Cell k={activeDay === "all" ? "Days · Total parcels" : "Day · Parcels"} v={activeDay === "all" ? `${plan.nDays} · ${totalParcels}` : `${dayLabelOf(activeDay)} · ${totalParcels}`} mono note={excluded.length ? `${inPlanParcels} in plan · ${excluded.length} street${excluded.length > 1 ? "s" : ""} out` : ""} />
            <Cell k="Streets" v={String(nominated.length)} mono />
          </div>

          {/* attention */}
          {badAddrs.length > 0 && (
            <div className="rse-addr">
              <div className="rse-addr-h">
                <span className="rse-addr-ic">✎</span>
                <span className="rse-addr-t">
                  {openAddrs.length ? `${openAddrs.length} address${openAddrs.length > 1 ? "es" : ""} to fix` : "All addresses fixed"}
                  <i> — the house number can't be right. No street here reaches four figures, so the customer has run
                  an apartment number into the street number. Correct it, or drop it if it's unusable.</i>
                </span>
                {badAddrs.length > openAddrs.length && (
                  <button className="rse-close-reset" onClick={() => setAddrByDriver((all) => ({ ...all, [driver]: {} }))}>
                    {badAddrs.length - openAddrs.length} done · Reset
                  </button>
                )}
              </div>
              <div className="rse-addr-l">
                {badAddrs.map((a) => {
                  const call = addrCalls[a.id];
                  const editing = editingAddr === a.id;
                  return (
                    <div key={a.id} className={"rse-addr-r" + (call ? " done" : "")}>
                      <div className="rse-addr-main">
                        <div className="rse-addr-raw">
                          {a.raw}
                          <span className="rse-addr-n" title={`Read as house number ${a.num} on ${a.street}`}>reads as #{a.num}</span>
                          {a.count > 1 && <span className="rse-addr-c">{a.count} parcels</span>}
                        </div>
                        {editing ? (
                          <div className="rse-addr-edit">
                            <input autoFocus className="rse-addr-in" value={editDraft}
                              onChange={(e) => setEditDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && editDraft.trim()) { setAddr(a.id, { to: editDraft.trim() }); setEditingAddr(null); }
                                if (e.key === "Escape") setEditingAddr(null);
                              }} />
                            <button className="rse-addr-save" disabled={!editDraft.trim()}
                              onClick={() => { setAddr(a.id, { to: editDraft.trim() }); setEditingAddr(null); }}>Save</button>
                            <button className="rse-addr-cancel" onClick={() => setEditingAddr(null)}>Cancel</button>
                          </div>
                        ) : call && call !== "remove" ? (
                          <div className="rse-addr-said">✓ Corrected to <b>{call.to}</b></div>
                        ) : call === "remove" ? (
                          <div className="rse-addr-said rse-addr-gone">✕ Dropped from {driver}'s plan</div>
                        ) : null}
                      </div>
                      {!editing && (
                        <div className="rse-close-acts">
                          {call
                            ? <button className="rse-close-undo" onClick={() => setAddr(a.id, null)}>Undo</button>
                            : <>
                                <button className="rse-close-b same" onClick={() => { setEditingAddr(a.id); setEditDraft(a.edit); }}
                                  title="Correct the address — the plan re-reads the street and number from it">Edit</button>
                                <button className="rse-close-b mis" onClick={() => setAddr(a.id, "remove")}
                                  title={`Drop this address from ${driver}'s plan entirely`}>Remove</button>
                              </>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {closePairs.length > 0 && (
            <div className="rse-close">
              <div className="rse-close-h">
                <span className="rse-close-ic">⇄</span>
                <span className="rse-close-t">
                  {openPairs.length ? `${openPairs.length} to check` : "All checked"} · two streets scanned seconds apart
                  <i> — {driver} can't have moved, so both parcels went to the same spot. A corner address with two
                  names, or a parcel on the wrong street?</i>
                </span>
                {closePairs.length > openPairs.length && (
                  <button className="rse-close-reset" onClick={() => setCloseByDriver((all) => ({ ...all, [driver]: {} }))}>
                    {closePairs.length - openPairs.length} decided · Reset
                  </button>
                )}
              </div>
              <div className="rse-close-l">
                {closePairs.map((p) => {
                  const call = closeCalls[p.id];
                  const { keep, other } = sideOf(p);
                  const h = p.hits[0];
                  return (
                    <div key={p.id} className={"rse-close-r" + (call ? " done" : "")}>
                      <div className="rse-close-main">
                        <div className="rse-close-pair">
                          <b>{titleCase(keep)}</b> <span className="x">⇄</span> <b>{titleCase(other)}</b>
                          <span className="rse-close-gap">{h.secs}s apart</span>
                          {p.nDays > 1 && <span className="rse-close-days">{p.nDays} days</span>}
                        </div>
                        <div className="rse-close-addr">{h.addrA} <span className="x">⇄</span> {h.addrB}</div>
                        {call && (
                          <div className="rse-close-said">
                            {call === "same" && <>✓ Same place — <b>{titleCase(other)}</b> folded into <b>{titleCase(keep)}</b> in the run order</>}
                            {call === "mis" && <>✕ Mis-delivery — <b>{titleCase(other)}</b> pulled out of {driver}'s plan</>}
                            {call === "ack" && <>Left as is</>}
                          </div>
                        )}
                      </div>
                      <div className="rse-close-acts">
                        {call
                          ? <button className="rse-close-undo" onClick={() => setCall(p.id, null)}>Undo</button>
                          : <>
                              <button className="rse-close-b same" onClick={() => setCall(p.id, "same")}
                                title={`One place, two names — merge ${titleCase(other)} into ${titleCase(keep)} as a single stop`}>Same place</button>
                              <button className="rse-close-b mis" onClick={() => setCall(p.id, "mis")}
                                title={`Wrong street — drop ${titleCase(other)} from ${driver}'s plan`}>Mis-delivery</button>
                              <button className="rse-close-b leave" onClick={() => setCall(p.id, "ack")}>Leave</button>
                            </>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {missing.length > 0 && (
            <div className="rse-missing">
              <button className="rse-missing-h" onClick={() => setShowMissing((v) => !v)} aria-expanded={showMissing}>
                <span className="rse-missing-ic">⚑</span>
                <span className="rse-missing-t">
                  {missing.length} street{missing.length > 1 ? "s" : ""} on no driver's route list
                  <b> · {missingParcels}/day</b> not in the sort plan
                </span>
                <span className="rse-missing-x">{showMissing ? "Hide" : "Show"}</span>
              </button>
              {showMissing && (
                <div className="rse-missing-l">
                  {missing.map((s) => {
                    const act = onRepair && registry[s.top]
                      ? () => {
                          if (window.confirm(`Add “${s.street}” to ${s.top}'s route list?\n\n${s.perDay}/day, delivered by ${s.drivers.map((d) => d.driver).join(", ")} over ${s.nDays} days — currently on no route list at all.\n\nSaves live to Driver_Routes and logs the change for AP.`))
                            onRepair({ type: "add", driver: s.top, street: s.street, fromDriver: null });
                        }
                      : null;
                    return (
                      <div key={s.key} className="rse-missing-r">
                        <span className="rse-missing-n mono">{s.perDay}<i>/day</i></span>
                        <span className="rse-missing-s">{s.street}</span>
                        <span className="rse-missing-d">
                          {s.drivers.slice(0, 3).map((d) => d.driver).join(", ")}
                          {s.drivers.length > 3 ? ` +${s.drivers.length - 3}` : ""}
                        </span>
                        {act && <button className="rse-fix" onClick={act}>Add to {s.top}</button>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className={"rse-flags" + (visibleFlags.length ? "" : " clean")}>
            <div className="rse-flags-h">
              <span className="rse-flags-ic">{visibleFlags.length ? "▲" : "✓"}</span>
              <span className="rse-flags-t">{visibleFlags.length ? `Needs attention · ${visibleFlags.length}` : "Sort plan looks clean — nothing flagged"}</span>
            </div>
            {visibleFlags.map((f, i) => {
              const isStreet = f.sev === "review" && f.idx != null; // street-level review flag, not "Section N empty" etc.
              const act = isStreet ? repairFor(f.ref) : null;
              const rem = isStreet ? removeFor(f.ref) : null;
              const cls = isStreet ? reviewStreet(f.ref, driver, registry, owners) : null;
              const st = isStreet ? nominated[f.idx] : null;
              const canonSt = st ? canonStreetName(st.key || st.street) : null;
              const onOwn = !!canonSt && !!regEntry && (regEntry.routes || []).some((rt) => rt.match === canonSt);
              const exactOthers = canonSt && owners[canonSt] ? [...owners[canonSt]].filter((d) => d !== driver) : [];
              // Range-split street that sits with other drivers by number → let him add HIS range to it.
              const canAddRange = isStreet && !!onRepair && !onOwn && !!cls && cls.flag === "OTHER" && (rangeOwners[canonSt] || []).length > 0;
              // Review numbers: his own street with out-of-range parcels, or a street that really sits with another driver.
              const showNums = !!st && !!onRepair && (st.numbers || []).length > 0 && (st.outOfRange > 0 || exactOthers.length > 0);
              // Suppress the blanket "Add to driver" on a real other-driver street (use Review numbers / remove / Add-with-range instead).
              const actBtn = (act && act.type === "add" && cls && cls.flag === "OTHER") ? null : act;
              // Correct/Bad: a flagged street that isn't his and isn't a real other-driver street → junk or a misspelling.
              const baseUnrec = isStreet && !!onRepair && !onOwn && exactOthers.length === 0 && !!cls && !!cls.flag;
              const unrec = baseUnrec || (isStreet && !!onRepair && !rem && !actBtn && !showNums && !canAddRange); // never leave a review flag with no button
              return (
                <React.Fragment key={i}>
                  <div className={"rse-flag " + f.sev + (f.idx != null && hover === f.idx ? " on" : "")}
                    onMouseEnter={() => f.idx != null && setHover(f.idx)} onMouseLeave={() => setHover(null)}>
                    <span className={"rse-sev " + f.sev}>{f.sev}</span>
                    <span className="rse-fref mono">{f.ref}</span>
                    <span className="rse-fmsg">{f.msg}</span>
                    {(rem || actBtn || unrec || showNums || canAddRange) && (
                      <span className="rse-fix-grp">
                        {rem && <button className="rse-fix rse-fix-rem" type="button" title={rem.confirm.split("\n")[0]} onClick={() => onRemoveClick(rem, f.ref)}>✓ {rem.label}</button>}
                        {canAddRange && <button className="rse-fix rse-fix-addr" type="button" title={`Add this street to ${driver}'s route with his number range`} onClick={() => startAddRange(f.ref, canonSt)}>➕ Add to {driver}</button>}
                        {showNums && <button className="rse-fix rse-fix-nums" type="button" title="See the house numbers and assign them to the correct driver by range" onClick={() => startNums(f.ref)}>⌗ Review numbers</button>}
                        {unrec && <button className="rse-fix rse-fix-corr" type="button" title="Match this to the correctly-spelled street on our routes" onClick={() => startCorrect(f.ref)}>✎ Correct spelling</button>}
                        {unrec && <button className="rse-fix rse-fix-bad" type="button" title="Discard as a bad / non-existent address" onClick={() => onBadAddress(f.ref)}>✕ Bad address</button>}
                        {actBtn && <button className="rse-fix" type="button" title={actBtn.confirm.split("\n")[0]} onClick={() => onFixClick(actBtn, f.ref)}>{actBtn.label}</button>}
                      </span>
                    )}
                  </div>
                  {reviewingNums === f.ref && st && (() => {
                    const myRoute = (regEntry?.routes || []).find((rt) => rt.match === canonSt);
                    const myRanges = myRoute && myRoute.ranges ? myRoute.ranges : null;
                    const inMine = (n) => !!myRanges && myRanges.some((rg) => n >= rg.lo && n <= rg.hi && (rg.parity == null || n % 2 === rg.parity));
                    const relevant = st.outOfRange > 0 ? (st.numbers || []).filter((n) => !inMine(n)) : (st.numbers || []);
                    const exactSet = canonSt && owners[canonSt] ? [...owners[canonSt]].filter((d) => d !== driver) : [];
                    const resolve = (n) => ownerForNumber(canonSt, n) || (exactSet.length === 1 ? exactSet[0] : null); // range, else the sole other owner
                    const groups = {};
                    relevant.forEach((n) => { const ow = resolve(n) || "—"; (groups[ow] = groups[ow] || []).push(n); });
                    const name = titleCase(st.key);
                    const driverOpts = Object.keys(registry).filter((d) => d !== driver).sort();
                    const entries = Object.entries(groups).sort((a, b) => (a[0] === "—" ? 1 : b[0] === "—" ? -1 : a[0].localeCompare(b[0])));
                    return (
                      <div className="rse-nums">
                        <div className="rse-nums-h">
                          <span>{name} — {relevant.length} address{relevant.length !== 1 ? "es" : ""} {st.outOfRange > 0 ? `outside ${driver}'s range` : "to assign"}</span>
                          <button className="rse-correct-x" type="button" onClick={() => setReviewingNums(null)}>Cancel</button>
                        </div>
                        <div className="rse-nums-list">
                          {entries.map(([ow, ns]) => (
                            <div key={ow} className="rse-nums-grp">
                              <div className="rse-nums-l">
                                <span className="to">{ow === "—" ? "No range — pick the driver" : `→ ${ow}`}</span>
                                <span className="nn mono">{ns.join(", ")}</span>
                              </div>
                              {ow === "—"
                                ? <span className="rse-nums-pick">
                                    <select className="rse-nums-sel" value={assignTo} onChange={(ev) => setAssignTo(ev.target.value)}>
                                      <option value="">driver…</option>
                                      {driverOpts.map((d) => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                    <button className="rse-add-btn" type="button" disabled={!assignTo} onClick={() => assignNums(st.key, assignTo, ns)}>Log →</button>
                                  </span>
                                : <button className="rse-add-btn" type="button" onClick={() => assignNums(st.key, ow, ns)}>Log → {ow}</button>}
                            </div>
                          ))}
                          {relevant.length === 0 && <div className="rse-correct-empty">Nothing to assign.</div>}
                        </div>
                        {!myRoute
                          ? <button className="rse-fix rse-fix-rem rse-nums-rm" type="button" onClick={() => { moveNamedStreet(f.ref, 0); setReviewingNums(null); }}>Remove “{name}” from {driver}'s plan</button>
                          : <button className="rse-fix rse-fix-nums rse-nums-rm" type="button" onClick={() => { markReviewed(f.ref); setReviewingNums(null); }}>✓ Mark “{name}” reviewed — clear flag</button>}
                      </div>
                    );
                  })()}
                  {addingRange === f.ref && (
                    <div className="rse-correct">
                      <div className="rse-correct-h">
                        <span>Add to {driver}'s route — include his number range</span>
                        <button className="rse-correct-x" type="button" onClick={() => setAddingRange(null)}>Cancel</button>
                      </div>
                      <div className="rse-addrange">
                        <input className="rse-correct-in" value={rangeInput} onChange={(e) => setRangeInput(e.target.value)}
                          placeholder="e.g. Elizabeth Street 302-512, 401-565" spellCheck={false}
                          onKeyDown={(e) => { if (e.key === "Enter") submitAddRange(); }} />
                        <button className="rse-add-btn" type="button" disabled={!rangeInput.trim()} onClick={submitAddRange}>Add</button>
                      </div>
                      <div className="rse-addrange-hint">Add the ranges (e.g. “302-512, 401-565”) so it only covers {driver}'s side — leave them off to take the whole street.</div>
                    </div>
                  )}
                  {correcting === f.ref && (
                    <div className="rse-correct">
                      <div className="rse-correct-h">
                        <span>Correct “{f.ref}” → pick the real street</span>
                        <button className="rse-correct-x" type="button" onClick={() => setCorrecting(null)}>Cancel</button>
                      </div>
                      <input className="rse-correct-in" value={correctQ} onChange={(e) => setCorrectQ(e.target.value)}
                        placeholder="Closest matches shown — or type to search…" spellCheck={false} />
                      <div className="rse-correct-list">
                        {correctSuggest.map((e) => {
                          const others = Object.keys(e.owners).filter((d) => d !== driver);
                          const onOwn = Object.keys(e.owners).includes(driver);
                          return (
                            <div key={e.canon} className="rse-correct-row">
                              <div className="rse-correct-l">
                                <span className="nm">{e.label}</span>
                                <span className="own">{onOwn ? "Your own street" : others.length ? `On ${others.join(", ")}` : "Not on any route"}</span>
                              </div>
                              <button className="rse-add-btn" type="button" onClick={() => applyCorrection(f.ref, e)}>
                                {onOwn ? "It's mine" : others.length ? `→ ${others[0]}` : "Use"}
                              </button>
                            </div>
                          );
                        })}
                        {correctSuggest.length === 0 && <div className="rse-correct-empty">No matches — try a different spelling, or use ✕ Bad address.</div>}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            {ackedFlags.length > 0 && (
              <div className="rse-acked">
                <button className="rse-acked-toggle" type="button" onClick={() => setShowAcked((s) => !s)}>
                  ✓ {ackedFlags.length} reviewed {showAcked ? "▴ hide" : "▾ show"}
                </button>
                {showAcked && ackedFlags.map((f, i) => (
                  <div key={"ack" + i} className="rse-flag review acked">
                    <span className="rse-sev review">done</span>
                    <span className="rse-fref mono">{f.ref}</span>
                    <span className="rse-fmsg">{f.msg}</span>
                    <span className="rse-fix-grp"><button className="rse-fix" type="button" onClick={() => unReview(f.ref)}>Undo</button></span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* add a street */}
          {onRepair && regEntry && (
            <div className="rse-add">
              <div className="rse-add-h">
                <span className="rse-add-t">➕ Add a street to {driver}'s list</span>
                <span className="rse-add-s">Search a recognised street — it shows which driver currently holds it, then add or move it onto {driver}.</span>
              </div>
              <input className="rse-add-in" value={addQ} onChange={(e) => setAddQ(e.target.value)}
                placeholder="Type a street name…" spellCheck={false} />
              {addQ.trim().length >= 2 && (
                <div className="rse-add-res">
                  {addMatches.map((e) => {
                    const others = Object.keys(e.owners).filter((d) => d !== driver);
                    const onCurrent = Object.keys(e.owners).includes(driver);
                    return (
                      <div key={e.canon} className="rse-add-row">
                        <div className="rse-add-l">
                          <span className="nm">{e.label}</span>
                          <span className="own">{onCurrent ? `Already on ${driver}` : others.length ? `On ${others.join(", ")}` : "Not on any route"}</span>
                        </div>
                        <div className="rse-add-a">
                          {onCurrent ? <span className="rse-add-have">✓ on list</span> : (
                            <>
                              {others.length === 1 && <button className="rse-add-btn move" onClick={() => addStreet("move", e, others[0])}>Move {others[0]} →</button>}
                              <button className="rse-add-btn" onClick={() => addStreet("add", e)}>{others.length ? "Add (shared)" : `Add to ${driver}`}</button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {!addExact && (
                    <div className="rse-add-row raw">
                      <div className="rse-add-l">
                        <span className="nm">“{addQ.trim()}”</span>
                        <span className="own">Not a recognised street — add as new</span>
                      </div>
                      <div className="rse-add-a"><button className="rse-add-btn" onClick={addRaw}>Add to {driver}</button></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* timeline */}
          <div className="rse-tl-wrap">
            <div className="rse-tl-head">
              <h3>Delivery flow — {plan.nDays > 1 ? `${plan.nDays}-day median` : "how the day ran"}</h3>
              <span>each bar = a street, first→last delivery</span>
            </div>
            <div className="rse-track">
              <div className="rse-grid">
                {axisTicks.map((t) => <div key={t} className="rse-gl" style={{ left: pct(t) + "%" }} />)}
                {axisTicks.map((t) => <div key={"l" + t} className="rse-gt" style={{ left: pct(t) + "%" }}>{fmtAxis(t)}</div>)}
              </div>
              {nominated.map((s, i) => {
                if (s.section === 0) return null; // excluded — not part of the day's flow
                const a = toMinutes(s.first), b = toMinutes(s.last);
                const meta = SECTION_META[s.section];
                return (
                  <div key={s.id} className={"rse-bar" + (hover === i ? " on" : "")}
                    style={{ left: pct(a) + "%", width: Math.max(pct(b) - pct(a), 2.2) + "%", top: 6 + (i % 7) * 18, background: meta.color }}
                    onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                    title={`${s.order}. ${s.street}${s.passes > 1 ? ` · pass ${s.pass} of ${s.passes}` : ""} · ${s.parcels}/day · ${s.first}–${s.last}`}>
                    <b>{s.key.split(" ")[0]}{s.passes > 1 ? ` (${s.pass})` : ""}</b>
                  </div>
                );
              })}
            </div>
          </div>

          {/* adjust */}
          <div className="rse-adjust">
            <span className="rse-hint">Drag a street between sections, tap A·B·C·D to reassign, or ✕ to pull it out of the plan.</span>
            {movedCount > 0 && <button className="rse-reset" onClick={() => setOvByDriver((all) => ({ ...all, [driver]: {} }))}>{movedCount} moved · Reset to auto</button>}
          </div>

          {movingTo && (
            <div className="rse-movebar">
              <span className="rse-movebar-t">
                {movingTo.kind === "section"
                  ? <>Move <b>Section {sectionCode(movingTo.section)}</b> — {sections[movingTo.section].length} street{sections[movingTo.section].length > 1 ? "s" : ""}, {sections[movingTo.section].reduce((a, s) => a + s.parcels, 0)} parcels — off {driver} to</>
                  : <>Move <b>{movingTo.label}</b> off {driver} to</>}
              </span>
              <select className="rse-movesel" value={moveDriver} onChange={(e) => setMoveDriver(e.target.value)}>
                {otherDrivers.map((d) => (
                  <option key={d} value={d}>{d}{registry[d]?.area && registry[d].area !== regEntry?.area ? ` · ${registry[d].area}` : ""}</option>
                ))}
              </select>
              <button className="rse-movego" onClick={doMove} disabled={!moveDriver}>Move</button>
              <button className="rse-movecancel" onClick={() => setMovingTo(null)}>Cancel</button>
              <span className="rse-movebar-w">Changes who delivers it · saves to Driver_Routes · logs one change for AP</span>
            </div>
          )}

          {/* sections */}
          <div className="rse-cols">
            {SECTION_NUMS.map((n) => {
              const meta = SECTION_META[n]; const sum = sectionSummary(n);
              return (
                <div key={n} className={"rse-col" + (dragOver === n ? " drop" : "")}
                  style={dragOver === n ? { boxShadow: `0 0 0 2px ${meta.color} inset`, borderColor: meta.color } : undefined}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(n); }}
                  onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(null); }}
                  onDrop={(e) => { e.preventDefault(); const st = e.dataTransfer.getData("text/plain"); if (st) moveStreet(st, n); setDragOver(null); }}>
                  <div className="rse-col-h" style={{ background: meta.color }}>
                    <div className="row1"><span className="lab">{meta.label}</span><span className="tag">{meta.tag}</span></div>
                    {sum && <><div className="win mono">{fmtWindow(sum.lo)} – {fmtWindow(sum.hi)}</div>
                      <div className="cnt mono">{sum.count} streets · {sum.parcels} parcels</div></>}
                    {sum && onRepair && otherDrivers.length > 0 && (
                      <button className="rse-colmove" onClick={() => startMove({ kind: "section", section: n })}
                        title={`Hand all of Section ${meta.code} to another driver — writes their route list and logs one change for AP`}>
                        ⇄ Move section to another driver
                      </button>
                    )}
                  </div>
                  {sections[n].length === 0 && <div className="rse-emptyc">Drop a street here</div>}
                  {sections[n].map((s) => {
                    const idx = nominated.indexOf(s);
                    return (
                      <div key={s.id} draggable
                        className={"rse-st" + (hover === idx ? " on" : "") + (s.moved ? " moved" : "")}
                        onDragStart={(e) => { e.dataTransfer.setData("text/plain", s.id); e.dataTransfer.effectAllowed = "move"; }}
                        onMouseEnter={() => setHover(idx)} onMouseLeave={() => setHover(null)}>
                        <div className="rse-st-l">
                          <div className="nm"><span className="rse-ord" title="Delivery order across the whole run">{s.order}</span> {s.street}
                            {s.passes > 1 && <span className="rse-pass" title={`${driver} works this street in ${s.passes} passes`}>pass {s.pass}/{s.passes}</span>}
                            {s.moved && <span className="rse-moved" title={`Auto: Section ${sectionCode(s.autoSection)}`}>moved</span>}
                          </div>
                          <div className="sub">{s.suburb}{s.shared ? `${s.suburb ? " · " : ""}shared · ${s.shared} drivers` : ""}</div>
                          {s.stopNum != null && <div className="rse-stop" title="Last number delivered before leaving the street — taken from the scans, not a map. Shown only because the days agree on it."><i>⏸</i> pauses at {s.stopNum}</div>}
                          {s.startNum != null && <div className="rse-stop rse-resume" title="First number delivered on returning to the street"><i>▶</i> resumes at {s.startNum}</div>}
                          {s.boundaryVaries && (
                            <div className="rse-stop rse-vary" title={`Where he breaks off moves about (${(s.stops || []).join(", ")}), or the passes don't split by number — so there's no boundary to give AP. The two passes are still real.`}>
                              <i>≈</i> boundary varies
                            </div>
                          )}
                          {(() => { const dn = sectionForTime(driverSecs, toMinutes(s.median)); return dn ? <div className="rse-dsec" title="Driver's own section name (from the scanner)"><i>▸</i> {dn}</div> : null; })()}
                        </div>
                        <div className="rse-st-r">
                          <div className="stat mono"><b>{s.parcels}</b> · {s.median}</div>
                          <div className="rse-pills">
                            {SECTION_NUMS.map((k) => (
                              <button key={k} className={"rse-pill" + (s.section === k ? " active" : "")}
                                style={s.section === k ? { background: SECTION_META[k].color, borderColor: SECTION_META[k].color } : undefined}
                                onClick={() => moveStreet(s.id, k)} aria-pressed={s.section === k} title={`Move to Section ${sectionCode(k)}`}>{sectionCode(k)}</button>
                            ))}
                            {onRepair && otherDrivers.length > 0 && (
                              <button className="rse-pill rse-give" onClick={() => startMove({ kind: "street", id: s.id, label: s.street })}
                                title={`Give ${s.street} to another driver — changes who delivers it, not just which bag`}>⇄</button>
                            )}
                            <button className="rse-pill rse-out" onClick={() => moveStreet(s.id, 0)}
                              title={`Pull ${s.street} out of ${driver}'s sort plan`}>✕</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {excluded.length > 0 && (
            <div className={"rse-excluded" + (dragOver === 0 ? " drop" : "")}
              onDragOver={(e) => { e.preventDefault(); setDragOver(0); }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(null); }}
              onDrop={(e) => { e.preventDefault(); const st = e.dataTransfer.getData("text/plain"); if (st) moveStreet(st, 0); setDragOver(null); }}>
              <div className="rse-excluded-h">
                <span className="rse-excluded-t">✕ Excluded — not in {driver}'s sort plan</span>
                <span className="rse-excluded-sub">{excluded.length} street{excluded.length > 1 ? "s" : ""} · {excludedParcels} parcels pulled out of Sections A·B·C·D. Tap A·B·C·D to put one back, or drag a street here to pull it out.</span>
              </div>
              <div className="rse-excluded-list">
                {excluded.map((s) => {
                  const idx = nominated.indexOf(s);
                  return (
                    <div key={s.id} draggable
                      className={"rse-xst" + (hover === idx ? " on" : "")}
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", s.id); e.dataTransfer.effectAllowed = "move"; }}
                      onMouseEnter={() => setHover(idx)} onMouseLeave={() => setHover(null)}>
                      <div className="rse-xst-l">
                        <div className="nm">{s.street}{s.passes > 1 && <span className="rse-pass">pass {s.pass}/{s.passes}</span>}</div>
                        <div className="sub">{s.suburb}{!s.onList ? `${s.suburb ? " · " : ""}off ${driver}'s list` : ""}{s.shared ? ` · shared · ${s.shared} drivers` : ""}</div>
                      </div>
                      <div className="rse-xst-r">
                        <div className="stat mono"><b>{s.parcels}</b> · {s.median}</div>
                        <div className="rse-pills">
                          {SECTION_NUMS.map((k) => (
                            <button key={k} className="rse-pill" onClick={() => moveStreet(s.id, k)}
                              title={`Put back into Section ${sectionCode(k)}`}>{sectionCode(k)}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="rse-foot">
            Streets are pulled from the audit's delivery scans, snapped to {driver}'s route list, numbered in the order {driver} drives them, and cut into four balanced sections. A street {driver} works in two goes appears twice, with the number it pauses at read off the scans. Adjust anything by dragging or tapping A·B·C·D.
            <br /><span className="dim">Section moves and exclusions are saved to your account (per driver), not pushed to the live sort plan. Adding or moving a street, though, updates the route list live and logs it for AP.</span>
          </div>
        </>
      )}
    </div>
  );
}

function Cell({ k, v, mono, dim, note }) {
  return (
    <div className="rse-cell">
      <div className="k">{k}</div>
      <div className={"v " + (mono ? "mono " : "") + (dim ? "dim" : "")}>{v}</div>
      {note ? <div className="note">{note}</div> : null}
    </div>
  );
}

const CSS = `
.rse{--ink:#16202B;--muted:#5C6875;--line:#E4E8ED;--panel:#fff;--bg:#F6F8FA;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:var(--ink);
  background:var(--bg);padding:22px;border-radius:14px;max-width:1080px;}
.rse *{box-sizing:border-box}
.rse.filedrag{box-shadow:inset 0 0 0 2px #1B9C84;}
.rse.filedrag .rse-drop{background:#1B9C84;color:#fff;border-color:#1B9C84;}
.rse .mono{font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;font-variant-numeric:tabular-nums;}
.rse-top{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;}
.rse-eyebrow{font-size:11px;letter-spacing:.18em;text-transform:uppercase;color:var(--muted);font-weight:600;}
.rse-title{font-size:23px;font-weight:700;margin:2px 0 0;letter-spacing:-.01em;}
.rse-load{display:flex;gap:10px;align-items:center;}
.rse-drop{font-size:12px;font-weight:700;color:#0C5A4C;background:#E3F4F0;border:1px solid #BFE6DD;
  padding:9px 15px;border-radius:9px;cursor:pointer;white-space:nowrap;}
.rse-drop:hover{background:#D6EFE9;}
.rse-sel{font-size:13px;font-weight:700;padding:8px 10px;border-radius:9px;border:1px solid var(--line);
  background:#fff;font-family:ui-monospace,monospace;cursor:pointer;}
.rse-retain{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:700;color:#0C5A4C;}
.rse-retain-dot{width:7px;height:7px;border-radius:50%;background:#1B9C84;display:inline-block;}
.rse-clear{font-size:10.5px;font-weight:700;color:#8A610F;background:#FBF1DC;border:1px solid #ECD9A8;padding:3px 9px;border-radius:20px;cursor:pointer;font-family:inherit;}
.rse-clear:hover{background:#F5E6BE;}
.rse-clear:disabled{opacity:.5;cursor:default;}
.rse-save{font-size:12px;font-weight:700;color:#0F6B57;background:#E6F5F1;border:1px solid #A9DDD1;padding:7px 13px;border-radius:20px;cursor:pointer;font-family:inherit;white-space:nowrap;}
.rse-save:hover{background:#D8EFE9;}.rse-save:disabled{opacity:.55;cursor:default;}
.rse-snaps-b{font-size:12px;font-weight:700;color:#5C6875;background:#fff;border:1px solid var(--line);padding:7px 12px;border-radius:20px;cursor:pointer;font-family:inherit;white-space:nowrap;}
.rse-snaps-b.on{background:#16202B;color:#fff;border-color:#16202B;}
.rse-snapnote{margin-top:12px;font-size:12.5px;font-weight:600;color:#0F6B57;background:#E6F5F1;border:1px solid #A9DDD1;padding:9px 14px;border-radius:9px;display:flex;justify-content:space-between;align-items:center;}
.rse-snapnote button{background:none;border:none;color:#0F6B57;cursor:pointer;font-size:13px;font-family:inherit;}
.rse-snaps{margin-top:14px;border:1px solid var(--line);border-radius:12px;background:#fff;overflow:hidden;}
.rse-snaps-h{padding:11px 15px;border-bottom:1px solid var(--line);background:#F7F9FB;}
.rse-snaps-h b{font-size:12.5px;}
.rse-snaps-h span{font-size:11px;color:var(--muted);margin-left:8px;}
.rse-snap{display:flex;align-items:center;gap:12px;padding:10px 15px;border-top:1px solid var(--line);}
.rse-snap:first-of-type{border-top:none;}
.rse-snap-l{flex:1;min-width:0;}
.rse-snap-t{font-size:13px;font-weight:700;}
.rse-snap-note{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#8A610F;background:#FBF1DC;border:1px solid #ECD9A8;border-radius:10px;padding:1px 6px;margin-left:7px;}
.rse-snap-s{font-size:10.5px;color:var(--muted);margin-top:2px;}
.rse-snap-open{font-size:11.5px;font-weight:700;color:#fff;background:#16202B;border:none;padding:6px 14px;border-radius:20px;cursor:pointer;font-family:inherit;}
.rse-snap-open:disabled{opacity:.5;cursor:default;}
.rse-snaps-cta{margin-top:14px;width:100%;font-size:12.5px;font-weight:700;color:#254A72;background:#F4F8FD;border:1px solid #BFD4EC;padding:11px 14px;border-radius:10px;cursor:pointer;font-family:inherit;text-align:left;}
.rse-snaps-cta:hover{background:#E9F1FA;}
.rse-export{font-size:11.5px;font-weight:700;color:#0C5A4C;background:#E3F4F0;border:1px solid #BFE6DD;padding:8px 13px;border-radius:9px;cursor:pointer;font-family:inherit;white-space:nowrap;}
.rse-export:hover{background:#D6EFE9;}.rse-export:disabled{opacity:.5;cursor:default;}
.rse-review{color:#8A2A2A;background:#FBE6E6;border-color:#F0C2C2;}.rse-review:hover{background:#F6D6D6;}
.rse-exp{display:inline-flex;align-items:center;gap:6px;}
.rse-expsel{font-size:11.5px;font-weight:600;padding:7px 8px;border-radius:9px;border:1px solid var(--line);background:#fff;font-family:inherit;cursor:pointer;max-width:160px;}
.rse-dsec{font-size:10px;font-weight:700;color:#5667C6;margin-top:2px;letter-spacing:.01em;}.rse-dsec i{font-style:normal;opacity:.55;}
.rse-err{margin-top:14px;font-size:12.5px;color:#9A3412;background:#FEF1E7;border:1px solid #F4CDB0;padding:10px 14px;border-radius:9px;}
.rse-empty-state{margin-top:26px;border:1.5px dashed #CDD5DD;border-radius:14px;padding:46px 24px;text-align:center;background:#fff;}
.rse-es-ic{font-size:34px;color:#9AA4AF;line-height:1;}
.rse-es-t{font-size:16px;font-weight:700;margin-top:12px;}
.rse-es-s{font-size:12.5px;color:var(--muted);margin-top:6px;max-width:440px;margin-left:auto;margin-right:auto;line-height:1.5;}
.rse-days{display:flex;align-items:center;flex-wrap:wrap;gap:6px;margin:18px 0 14px;}
.rse-days-l{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:var(--muted);margin-right:4px;}
.rse-day{font-size:12px;font-weight:700;padding:6px 12px;border-radius:20px;border:1px solid var(--line);background:#fff;color:var(--muted);cursor:pointer;font-family:inherit;}
.rse-day:hover{background:#F4F7F9;}
.rse-day.on{background:#16202B;border-color:#16202B;color:#fff;}
.rse-meta{display:grid;grid-template-columns:repeat(6,1fr);gap:1px;background:var(--line);border:1px solid var(--line);
  border-radius:10px;overflow:hidden;margin:0 0 22px;}
.rse-cell{background:#fff;padding:11px 13px;}
.rse-cell .k{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);font-weight:600;}
.rse-cell .v{font-size:16px;font-weight:700;margin-top:3px;}
.rse-cell .v.dim{color:#9AA4AF;}
.rse-cell .note{font-size:9.5px;color:#9AA4AF;margin-top:1px;}
.rse-addr{border:1px solid #E0CDA8;border-radius:12px;overflow:hidden;margin-bottom:14px;background:#FDFAF3;}
.rse-addr-h{display:flex;align-items:flex-start;gap:9px;padding:11px 15px;}
.rse-addr-ic{color:#8A610F;font-size:14px;font-weight:800;line-height:1.3;}
.rse-addr-t{font-size:12.5px;font-weight:700;color:#6B4C0B;flex:1;line-height:1.45;}
.rse-addr-t i{font-style:normal;font-weight:500;color:#8A7645;}
.rse-addr-l{border-top:1px solid #EFE2C6;}
.rse-addr-r{display:flex;align-items:center;gap:12px;padding:9px 15px;border-top:1px solid #F4EBD8;}
.rse-addr-r:first-child{border-top:none;}
.rse-addr-r.done{background:#FEFDFA;}
.rse-addr-main{flex:1;min-width:0;}
.rse-addr-raw{font-size:12px;font-family:ui-monospace,monospace;color:#16202B;}
.rse-addr-n{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#9A3412;background:#FDEDE6;border-radius:10px;padding:1px 6px;margin-left:7px;font-family:inherit;}
.rse-addr-c{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#5C6875;background:#EEF1F4;border-radius:10px;padding:1px 6px;margin-left:4px;font-family:inherit;}
.rse-addr-said{font-size:10.5px;font-weight:600;color:#0F6B57;margin-top:3px;}
.rse-addr-said.rse-addr-gone{color:#9A3412;}
.rse-addr-edit{display:flex;gap:6px;margin-top:6px;}
.rse-addr-in{flex:1;font-size:12px;font-family:ui-monospace,monospace;padding:6px 9px;border:1px solid #D9C79B;border-radius:7px;background:#fff;color:#16202B;}
.rse-addr-in:focus{outline:none;border-color:#8A610F;box-shadow:0 0 0 2px rgba(138,97,15,.12);}
.rse-addr-save{font-size:11px;font-weight:700;color:#fff;background:#1B9C84;border:none;padding:6px 12px;border-radius:7px;cursor:pointer;font-family:inherit;}
.rse-addr-save:disabled{opacity:.45;cursor:default;}
.rse-addr-cancel{font-size:11px;font-weight:700;color:var(--muted);background:none;border:none;cursor:pointer;font-family:inherit;}
.rse-close{border:1px solid #BFD4EC;border-radius:12px;overflow:hidden;margin-bottom:14px;background:#F4F8FD;}
.rse-close-h{display:flex;align-items:flex-start;gap:9px;padding:11px 15px;}
.rse-close-ic{color:#3B6FA8;font-size:14px;font-weight:800;line-height:1.3;}
.rse-close-t{font-size:12.5px;font-weight:700;color:#254A72;flex:1;line-height:1.45;}
.rse-close-t i{font-style:normal;font-weight:500;color:#5C7898;}
.rse-close-reset{font-size:11px;font-weight:700;color:#254A72;background:#fff;border:1px solid #BFD4EC;padding:4px 10px;border-radius:20px;cursor:pointer;white-space:nowrap;font-family:inherit;}
.rse-close-l{border-top:1px solid #D9E5F3;}
.rse-close-r{display:flex;align-items:center;gap:12px;padding:9px 15px;border-top:1px solid #E4EDF7;}
.rse-close-r:first-child{border-top:none;}
.rse-close-r.done{background:#FAFCFE;}
.rse-close-main{flex:1;min-width:0;}
.rse-close-pair{font-size:12.5px;color:#16202B;}
.rse-close-pair .x{color:#8FA8C4;margin:0 3px;}
.rse-close-gap{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#3B6FA8;background:#E4EDF7;border-radius:10px;padding:1px 6px;margin-left:7px;}
.rse-close-days{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#7A4A12;background:#FBF1DC;border-radius:10px;padding:1px 6px;margin-left:4px;}
.rse-close-addr{font-size:10px;color:var(--muted);font-family:ui-monospace,monospace;margin-top:2px;}
.rse-close-said{font-size:10.5px;font-weight:600;color:#3B6FA8;margin-top:3px;}
.rse-close-acts{display:flex;gap:5px;flex-shrink:0;}
.rse-close-b{font-size:11px;font-weight:700;padding:5px 10px;border-radius:20px;cursor:pointer;font-family:inherit;white-space:nowrap;border:1px solid;}
.rse-close-b.same{color:#0F6B57;background:#E6F5F1;border-color:#A9DDD1;}
.rse-close-b.mis{color:#9A3412;background:#FDEDE6;border-color:#F0C4AE;}
.rse-close-b.leave{color:var(--muted);background:#fff;border-color:var(--line);}
.rse-close-b:hover{transform:translateY(-1px);}
.rse-close-undo{font-size:11px;font-weight:700;color:var(--muted);background:none;border:none;cursor:pointer;font-family:inherit;text-decoration:underline;}
.rse-missing{border:1px solid #E8B4A0;border-radius:12px;overflow:hidden;margin-bottom:14px;background:#FDF4F0;}
.rse-missing-h{display:flex;align-items:center;gap:9px;padding:11px 15px;width:100%;background:none;border:none;cursor:pointer;font-family:inherit;text-align:left;}
.rse-missing-ic{color:#C0563E;font-size:13px;}
.rse-missing-t{font-size:12.5px;color:#7C3A22;flex:1;}.rse-missing-t b{color:#C0563E;}
.rse-missing-x{font-size:11px;font-weight:700;color:#C0563E;}
.rse-missing-l{border-top:1px solid #F0D6CB;}
.rse-missing-r{display:flex;align-items:center;gap:11px;padding:7px 15px;border-top:1px solid #F6E5DE;}
.rse-missing-r:first-child{border-top:none;}
.rse-missing-n{font-size:13px;font-weight:800;color:#C0563E;min-width:52px;text-align:right;}
.rse-missing-n i{font-style:normal;font-size:9px;font-weight:600;opacity:.7;}
.rse-missing-s{font-size:12.5px;font-weight:600;flex:1;}
.rse-missing-d{font-size:10px;color:var(--muted);font-family:ui-monospace,monospace;}
.rse-flags{border:1px solid var(--line);border-radius:12px;overflow:hidden;margin-bottom:22px;background:#fff;}
.rse-flags.clean{border-color:#CDE7DF;background:#F3FAF8;}
.rse-flags-h{display:flex;align-items:center;gap:8px;padding:11px 15px;border-bottom:1px solid var(--line);}
.rse-flags.clean .rse-flags-h{border-bottom:none;}
.rse-flags-ic{font-size:11px;color:#B8761B;}.rse-flags.clean .rse-flags-ic{color:#1B9C84;}
.rse-flags-t{font-size:12.5px;font-weight:700;}.rse-flags.clean .rse-flags-t{color:#0C5A4C;}
.rse-flag{display:flex;align-items:baseline;gap:10px;padding:10px 15px;border-top:1px solid #EEF1F4;transition:background .12s;}
.rse-flag:first-of-type{border-top:none;}.rse-flag.on{background:#F2F5F8;}
.rse-sev{flex-shrink:0;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;padding:2px 7px;border-radius:20px;color:#fff;}
.rse-sev.review{background:#B8761B;}.rse-sev.watch{background:#6B7785;}
.rse-fref{flex-shrink:0;font-size:12px;font-weight:700;min-width:96px;}
.rse-fmsg{font-size:12px;color:var(--muted);line-height:1.45;}
.rse-fix{align-self:center;margin-left:auto;flex-shrink:0;font-size:11px;font-weight:700;color:#0C5A4C;background:#E3F4F0;border:1px solid #BFE6DD;padding:5px 11px;border-radius:8px;cursor:pointer;font-family:inherit;white-space:nowrap;}
.rse-fix:hover{background:#CDEBE3;}
.rse-fix-grp{margin-left:auto;display:flex;gap:6px;align-items:center;flex-shrink:0;}
.rse-fix-grp .rse-fix{margin-left:0;}
.rse-fix-rem{color:#8A2A2A;background:#FBE6E6;border-color:#F0C2C2;}
.rse-fix-rem:hover{background:#F6D6D6;}
.rse-fix-corr{color:#3A4D8F;background:#EAEEF9;border-color:#C7D2EE;}
.rse-fix-corr:hover{background:#DCE3F4;}
.rse-fix-bad{color:#8A4B0C;background:#FBEFE0;border-color:#EFD3AE;}
.rse-fix-bad:hover{background:#F5E2C8;}
.rse-fix-nums{color:#0C5A4C;background:#E3F4F0;border-color:#BFE6DD;}
.rse-fix-nums:hover{background:#D2ECE6;}
.rse-fix-addr{color:#0C5A4C;background:#E3F4F0;border-color:#BFE6DD;}
.rse-fix-addr:hover{background:#D2ECE6;}
.rse-addrange{display:flex;gap:7px;align-items:center;}
.rse-addrange .rse-correct-in{flex:1;}
.rse-addrange-hint{font-size:10.5px;color:var(--muted);margin-top:7px;line-height:1.4;}
.rse-acked{border-top:1px solid #EEF1F4;}
.rse-acked-toggle{width:100%;text-align:left;font-size:11px;font-weight:700;color:#0C5A4C;background:#F3FAF8;border:none;padding:9px 15px;cursor:pointer;font-family:inherit;}
.rse-acked-toggle:hover{background:#E9F5F1;}
.rse-flag.acked{opacity:.6;}
.rse-flag.acked .rse-sev.review{background:#1B9C84;}
.rse-nums{margin:0 15px 12px;background:#F9FBFC;border:1px solid var(--line);border-radius:10px;padding:11px 13px;}
.rse-nums-h{display:flex;justify-content:space-between;align-items:center;font-size:11.5px;font-weight:700;color:var(--ink);margin-bottom:8px;gap:10px;}
.rse-nums-list{display:flex;flex-direction:column;border:1px solid var(--line);border-radius:8px;overflow:hidden;background:#fff;}
.rse-nums-grp{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:8px 11px;border-top:1px solid #EEF1F4;}
.rse-nums-grp:first-child{border-top:none;}
.rse-nums-l{min-width:0;display:flex;flex-direction:column;gap:2px;}
.rse-nums-l .to{font-size:12px;font-weight:700;}
.rse-nums-l .nn{font-size:11.5px;color:var(--muted);word-break:break-word;}
.rse-nums-warn{font-size:10.5px;font-weight:700;color:#9A3412;background:#FEECE2;border:1px solid #F4CDB0;border-radius:7px;padding:3px 8px;white-space:nowrap;}
.rse-nums-pick{display:inline-flex;gap:5px;align-items:center;flex-shrink:0;}
.rse-nums-sel{font-size:11.5px;font-family:inherit;padding:5px 7px;border-radius:7px;border:1px solid var(--line);background:#fff;cursor:pointer;}
.rse-nums-rm{margin-top:9px;}
.rse-correct{margin:0 15px 12px;background:#F9FBFC;border:1px solid var(--line);border-radius:10px;padding:11px 13px;}
.rse-correct-h{display:flex;justify-content:space-between;align-items:center;font-size:11.5px;font-weight:700;color:var(--ink);margin-bottom:8px;gap:10px;}
.rse-correct-x{font-size:10.5px;font-weight:700;color:var(--muted);background:#fff;border:1px solid var(--line);border-radius:7px;padding:3px 9px;cursor:pointer;font-family:inherit;flex-shrink:0;}
.rse-correct-in{width:100%;font-size:12.5px;font-family:inherit;padding:7px 10px;border:1px solid var(--line);border-radius:8px;background:#fff;}
.rse-correct-in:focus{outline:none;border-color:#5667C6;box-shadow:0 0 0 2px rgba(86,103,198,.14);}
.rse-correct-list{margin-top:8px;display:flex;flex-direction:column;border:1px solid var(--line);border-radius:8px;overflow:hidden;background:#fff;}
.rse-correct-row{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:7px 11px;border-top:1px solid #EEF1F4;}
.rse-correct-row:first-child{border-top:none;}
.rse-correct-l{min-width:0;display:flex;flex-direction:column;gap:1px;}
.rse-correct-l .nm{font-size:12.5px;font-weight:600;}
.rse-correct-l .own{font-size:10px;color:var(--muted);}
.rse-correct-empty{padding:10px 11px;font-size:11px;color:var(--muted);font-style:italic;}
.rse-add{border:1px solid var(--line);border-radius:12px;background:#fff;padding:14px 16px;margin-bottom:22px;}
.rse-add-h{display:flex;flex-direction:column;gap:2px;margin-bottom:10px;}
.rse-add-t{font-size:13px;font-weight:800;color:var(--ink);}
.rse-add-s{font-size:11px;color:var(--muted);line-height:1.4;}
.rse-add-in{width:100%;font-size:13px;font-family:inherit;padding:9px 12px;border:1px solid var(--line);border-radius:9px;background:#F9FBFC;}
.rse-add-in:focus{outline:none;border-color:#1B9C84;background:#fff;box-shadow:0 0 0 2px rgba(27,156,132,.14);}
.rse-add-res{margin-top:10px;border:1px solid var(--line);border-radius:10px;overflow:hidden;}
.rse-add-row{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:9px 13px;border-top:1px solid #EEF1F4;}
.rse-add-row:first-child{border-top:none;}
.rse-add-row.raw{background:#FCFBF6;}
.rse-add-l{min-width:0;display:flex;flex-direction:column;gap:1px;}
.rse-add-l .nm{font-size:13px;font-weight:600;}
.rse-add-l .own{font-size:10.5px;color:var(--muted);}
.rse-add-a{flex-shrink:0;display:flex;gap:6px;align-items:center;}
.rse-add-btn{font-size:11px;font-weight:700;color:#0C5A4C;background:#E3F4F0;border:1px solid #BFE6DD;padding:5px 11px;border-radius:8px;cursor:pointer;font-family:inherit;white-space:nowrap;}
.rse-add-btn:hover{background:#CDEBE3;}
.rse-add-btn.move{color:#5A3B0C;background:#FBF1DC;border-color:#ECD9A8;}.rse-add-btn.move:hover{background:#F5E6BE;}
.rse-add-have{font-size:10.5px;font-weight:700;color:#9AA4AF;}
.rse-tl-wrap{background:#fff;border:1px solid var(--line);border-radius:12px;padding:18px 20px 12px;margin-bottom:22px;}
.rse-tl-head{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:16px;}
.rse-tl-head h3{margin:0;font-size:13px;font-weight:700;}.rse-tl-head span{font-size:11px;color:var(--muted);}
.rse-track{position:relative;height:148px;}
.rse-grid{position:absolute;inset:0 0 22px 0;}
.rse-gl{position:absolute;top:0;bottom:0;width:1px;background:var(--line);}
.rse-gt{position:absolute;bottom:2px;transform:translateX(-50%);font-size:10px;color:#9AA4AF;}
.rse-bar{position:absolute;height:18px;border-radius:5px;display:flex;align-items:center;padding:0 6px;overflow:hidden;
  transition:transform .12s,box-shadow .12s;box-shadow:0 1px 0 rgba(0,0,0,.04);}
.rse-bar:hover,.rse-bar.on{transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,.16);z-index:5;}
.rse-bar b{font-size:10px;font-weight:700;color:#fff;white-space:nowrap;text-shadow:0 1px 1px rgba(0,0,0,.25);}
.rse-adjust{display:flex;justify-content:space-between;align-items:center;gap:10px;margin:0 2px 11px;}
.rse-hint{font-size:11.5px;color:var(--muted);}
.rse-reset{font-size:11px;font-weight:700;color:#8A610F;background:#FBF1DC;border:1px solid #ECD9A8;padding:5px 11px;border-radius:20px;cursor:pointer;white-space:nowrap;}
.rse-cols{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;}
.rse-col{background:#fff;border:1px solid var(--line);border-radius:12px;overflow:hidden;display:flex;flex-direction:column;}
.rse-col-h{padding:13px 15px;color:#fff;}
.rse-col-h .row1{display:flex;justify-content:space-between;align-items:center;}
.rse-col-h .lab{font-size:15px;font-weight:800;}.rse-col-h .tag{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;background:rgba(255,255,255,.22);padding:3px 8px;border-radius:20px;}
.rse-col-h .win{font-size:12px;margin-top:7px;font-weight:600;}.rse-col-h .cnt{font-size:11px;margin-top:2px;opacity:.85;}
.rse-st{padding:9px 14px;border-top:1px solid var(--line);display:flex;justify-content:space-between;align-items:center;gap:8px;transition:background .12s;cursor:grab;}
.rse-st:active{cursor:grabbing;}.rse-st:first-of-type{border-top:none;}.rse-st.on{background:#F2F5F8;}.rse-st.moved{background:#FCFBF6;}
.rse-st-l{min-width:0;flex:1;}
.rse-st .nm{font-size:13px;font-weight:600;line-height:1.2;}
.rse-st .sub{font-size:10px;color:var(--muted);margin-top:1px;}
.rse-st-r{flex-shrink:0;display:flex;flex-direction:column;align-items:flex-end;gap:5px;}
.rse-st .stat{font-size:11px;color:var(--muted);white-space:nowrap;}.rse-st .stat b{font-size:13px;color:var(--ink);font-weight:700;}
.rse-moved{font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#fff;background:#94A1AE;border-radius:10px;padding:1px 6px;margin-left:6px;vertical-align:1px;}
.rse-ord{display:inline-flex;align-items:center;justify-content:center;min-width:17px;height:17px;padding:0 4px;border-radius:5px;background:#16202B;color:#fff;font-size:10px;font-weight:800;font-variant-numeric:tabular-nums;vertical-align:1px;}
.rse-pass{font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#7A4A12;background:#FBF1DC;border:1px solid #ECD9A8;border-radius:10px;padding:0 5px;margin-left:5px;vertical-align:1px;white-space:nowrap;}
.rse-stop{font-size:10px;font-weight:700;color:#9A3412;margin-top:2px;letter-spacing:.01em;}
.rse-stop i{font-style:normal;opacity:.6;margin-right:2px;}
.rse-stop.rse-resume{color:#1B9C84;}
.rse-stop.rse-vary{color:#94A1AE;font-weight:600;}
.rse-pills{display:flex;gap:3px;}
.rse-pill{width:20px;height:20px;border-radius:6px;border:1px solid var(--line);background:#fff;font-size:10px;font-weight:700;color:var(--muted);cursor:pointer;display:flex;align-items:center;justify-content:center;font-family:inherit;transition:transform .1s;}
.rse-pill:hover{transform:translateY(-1px);}.rse-pill.active{color:#fff;}
.rse-pill.rse-out{color:#9A3412;font-weight:800;}
.rse-pill.rse-give{color:#254A72;font-weight:800;border-color:#BFD4EC;background:#F4F8FD;}
.rse-colmove{margin-top:8px;width:100%;font-size:10.5px;font-weight:700;color:#fff;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.35);padding:4px 8px;border-radius:20px;cursor:pointer;font-family:inherit;}
.rse-colmove:hover{background:rgba(255,255,255,.3);}
.rse-movebar{display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-bottom:12px;padding:11px 15px;border:1px solid #BFD4EC;background:#F4F8FD;border-radius:12px;}
.rse-movebar-t{font-size:12.5px;color:#254A72;}
.rse-movesel{font-size:12px;font-weight:700;padding:6px 9px;border:1px solid #BFD4EC;border-radius:8px;background:#fff;font-family:ui-monospace,monospace;color:#16202B;}
.rse-movego{font-size:12px;font-weight:700;color:#fff;background:#254A72;border:none;padding:7px 15px;border-radius:20px;cursor:pointer;font-family:inherit;}
.rse-movego:disabled{opacity:.5;cursor:default;}
.rse-movecancel{font-size:12px;font-weight:700;color:var(--muted);background:none;border:none;cursor:pointer;font-family:inherit;}
.rse-movebar-w{font-size:10.5px;color:#5C7898;margin-left:auto;}
.rse-pill.rse-out:hover{background:#FEECE2;border-color:#F4CDB0;color:#9A3412;}
.rse-emptyc{padding:18px 14px;font-size:12px;color:#9AA4AF;text-align:center;font-style:italic;}
.rse-excluded{margin-top:16px;background:#fff;border:1px dashed #CDD5DD;border-radius:12px;overflow:hidden;}
.rse-excluded.drop{border-style:solid;border-color:#6B7785;box-shadow:0 0 0 2px rgba(107,119,133,.2) inset;}
.rse-excluded-h{padding:12px 16px;background:#F6F8FA;border-bottom:1px solid var(--line);}
.rse-excluded-t{font-size:13px;font-weight:800;color:#5C6875;}
.rse-excluded-sub{display:block;font-size:11px;color:#9AA4AF;margin-top:3px;line-height:1.45;}
.rse-excluded-list{display:flex;flex-direction:column;}
.rse-xst{padding:9px 16px;border-top:1px solid var(--line);display:flex;justify-content:space-between;align-items:center;gap:8px;cursor:grab;}
.rse-xst:first-of-type{border-top:none;}.rse-xst:active{cursor:grabbing;}.rse-xst.on{background:#F2F5F8;}
.rse-xst-l{min-width:0;flex:1;}
.rse-xst .nm{font-size:13px;font-weight:600;color:#5C6875;text-decoration:line-through;text-decoration-color:#C4CCD4;}
.rse-xst .sub{font-size:10px;color:var(--muted);margin-top:1px;}
.rse-xst-r{flex-shrink:0;display:flex;flex-direction:column;align-items:flex-end;gap:5px;}
.rse-xst .stat{font-size:11px;color:var(--muted);white-space:nowrap;}.rse-xst .stat b{font-size:13px;color:var(--ink);font-weight:700;}
.rse-foot{margin-top:18px;font-size:12px;color:var(--muted);line-height:1.55;}.rse-foot .dim{color:#9AA4AF;}
@media (max-width:1180px){.rse-cols{grid-template-columns:repeat(2,1fr);}}
@media (max-width:760px){.rse-meta{grid-template-columns:repeat(2,1fr);}.rse-cols{grid-template-columns:1fr;}.rse-flag{flex-wrap:wrap;gap:4px 10px;}.rse-fmsg{flex-basis:100%;}}
@media (prefers-reduced-motion:reduce){.rse-bar,.rse-st,.rse-pill{transition:none;}}
`;
